<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->
<template>
  <div
    class="dashboard-item"
    :class="{ 'dashboard-item-wide': wideLayout }"
    :style="widgetGridStyle"
  >
    <div v-if="hasError" class="dashboard-widget-error">Unable to load widget.</div>
    <template v-else>
      <div class="header-container">
      <div
        class="title mr-auto"
        draggable="false"
        @pointerdown.stop="$emit('widgetdragstart', $event)"
        style="cursor: grab; touch-action: none"
      >
        <img v-if="widgetIcon" :src="widgetIcon" alt="" height="24" />
        <div class="widget-title-text">
          <div class="d-flex flex-wrap" style="gap: 0.25rem" v-html="widgetTitle"></div>
          <div v-if="activeFilterCount > 0" class="widget-filter-subtext" :title="activeFilterTooltip">
            {{ activeFilterText }}
          </div>
        </div>
      </div>
      <span
        class="menu-btn"
        v-if="widgetType == 'Notifications'"
        title="Mark all as Read"
        @click="updateAllNotificationsIsRead"
      >
        <img src="https://dsmlib.logistics.systems/Icons/open-mail.png" alt="Mark as Read" width="16" />
      </span>
      <router-link v-if="!editMode && widgetLink" :to="widgetLink" class="menu-btn">
        <img src="https://dsmlib.logistics.systems/Icons/open-in-new-tab.png" alt="" height="16" />
      </router-link>
      <div
        class="context-menu-wrapper"
        ref="contextMenu"
        v-if="!widgetObjectEmpty"
        :class="{ active: contextMenuIsOpen }"
      >
        <Button class="menu-btn" @click="contextMenuBtnClick"
          ><img
            src="https://dsmlib.logistics.systems/Icons/icons8-ellipsis-100.png"
            alt=""
            height="12"
            style="transform: rotate(90deg)"
        /></Button>
        <div class="context-menu-list" role="menu">
          <button type="button" class="context-menu-item" @click="refreshClicked">Refresh</button>
          <hr class="my-0" />
          <button type="button" class="context-menu-item" @click="widgetEditClicked">Edit Widget</button>
          <button type="button" class="context-menu-item" @click="widgetDuplicateClicked">Duplicate Widget</button>
          <button type="button" class="context-menu-item" @click="removeClicked">Remove Widget</button>
        </div>
      </div>
    </div>
    <span
      v-if="canResizeWidget"
      class="widget-resize-handle"
      aria-label="Resize widget"
      title="Resize widget"
      @pointerdown.stop.prevent="$emit('widgetresizestart', $event)"
    >
      <span class="widget-resize-icon"></span>
    </span>

    <!-- edit  -->

    <div class="edit-container" v-if="editMode || widgetObjectEmpty" ref="editContainer">
      <Dropdown
        placeholder="Widget"
        :items="widgetListFiltered"
        itemKey="title"
        :selectedObject.sync="widgetEditValue"
        :errorMessage.sync="widgetEditValueError"
        @selected="widgetEditSelected"
      />
      <Dropdown
        placeholder="Widget Type"
        :items="widgetTypeListFiltered"
        itemKey="label"
        :selectedObject.sync="widgetTypeEditValue"
        :errorMessage.sync="widgetTypeEditValueError"
        v-if="showWidgetTypeDropdown"
        @selected="widgetTypeSelected"
      />
      <div v-if="widgetTypeEditValue && widgetTemplateList.length > 0" class="widget-template-config">
        <Dropdown
          :key="`widget-template-${widgetTypeEditValue.value}`"
          placeholder="Template"
          :items="widgetTemplateList"
          itemKey="label"
          :selectedObject.sync="widgetTemplateValue"
          @selected="widgetTemplateSelected"
        />
      </div>
      <hr
        v-if="widgetTypeEditValue && widgetTypeEditValue.value != 'Search'"
        class="edit-divider"
      />

      <div v-if="widgetTypeEditValue">
        <div v-if="widgetTypeEditValue.value == 'List'" class="edit-widget-fields d-flex flex-column">
          <div>Primary Fields</div>
          <div class="flex-container">
            <Dropdown
              placeholder="Left Field"
              :items="editFieldsLeftListFiltered"
              itemKey="label"
              :selectedObject.sync="primaryLeftFieldValue"
              :errorMessage.sync="primaryLeftFieldValueError"
              @selected="markTemplateCustom"
            />
            <Dropdown
              placeholder="Right Field"
              :items="editFieldsListFiltered"
              itemKey="label"
              :selectedObject.sync="primaryRightFieldValue"
              @selected="markTemplateCustom"
            />
          </div>
          <div class="list-fields-editor">
            <div>Fields</div>
            <div class="field-config list-field-config" ref="listFieldConfig">
              <div
                class="field-config-item"
                v-for="(col, index) in detailFieldsEdit"
                :key="col.id || col.key"
                :data-field-key="col.key"
                :class="{ dragging: dragIndex === index }"
                :style="{ gridColumn: `span ${col.span ? col.span : '1'}` }"
                draggable="true"
                @dragstart.stop="onFieldConfigItemDragStart(index, col)"
                @dragover.prevent.stop="onFieldConfigItemDragOver(index, $event)"
                @dragend.stop="onFieldConfigItemDragEnd"
                @touchstart.stop="onFieldConfigItemTouchStart(index, col, $event)"
                @touchmove.stop.prevent="onFieldConfigItemTouchMove"
                @touchend.stop="onFieldConfigItemTouchEnd"
                @touchcancel.stop="onFieldConfigItemTouchEnd"
              >
                <div class="field-config-item-label">{{ col.label }}</div>
                <button type="button" class="field-config-item-delete" @click="detailsFieldDelete(index)" title="Remove field">
                  <img src="https://dsmlib.logistics.systems/Icons/icons8-minus-48.png" alt="" height="15" />
                </button>
              </div>
            </div>
            <Dropdown
              v-if="detailFieldsEdit.length < 9"
              placeholder="New Field"
              :items="editFieldsListFiltered"
              itemKey="label"
              @selected="editNewFieldSelected"
              :search.sync="editNewFieldInputValue"
            />
            <div v-else style="color: darkred; font-size: 0.6rem; font-style: italic"
              >Max fields added. Unable to add more</div
            >
          </div>
          <div class="flex-container">
            <Dropdown
              placeholder="Sort By"
              :items="editFieldsListSort"
              :selectedObject.sync="sortByValue"
              :errorMessage.sync="sortByValueError"
              itemKey="label"
              @selected="markTemplateCustom"
            />
            <CheckBox title="Descending" :isChecked.sync="sortByDescValue" @checkBoxClicked="markTemplateCustom" />
          </div>
          <hr class="edit-divider" />
          <FiltersGroup
            v-if="listFilterDefinition.length > 0"
            :value.sync="filterValue"
            :filterDefinition="listFilterDefinition"
            @update:value="markTemplateCustom"
          />
          <Input
            placeholder="Max Items"
            validator="positiveInteger"
            :inputValue.sync="maxRowsValue"
            @focusChanged="maxRowsFocusLost"
            @inputChanged="markTemplateCustom"
          />
        </div>

        <div v-if="widgetTypeEditValue.value == 'Chart'" class="edit-widget-fields d-flex flex-column">
          <Dropdown
            v-if="chartGroupList.length > 0"
            placeholder="Chart Group"
            :items="chartGroupList"
            itemKey="label"
            :selectedObject.sync="chartGroupValue"
            :errorMessage.sync="chartGroupValueError"
            @selected="chartGroupSelected"
          />
          <Dropdown
            placeholder="Chart Type"
            :items="chartWidgetTypeListFiltered"
            itemKey="label"
            :selectedObject.sync="chartTypeValue"
            :errorMessage.sync="chartTypeValueError"
            @selected="markTemplateCustom"
          />

          <hr class="edit-divider" />
          <div class="chart-time-config">
            <div>Date scope</div>
            <Dropdown
              placeholder="Date scope"
              :items="chartDateScopeList"
              itemKey="label"
              :selectedObject.sync="chartDateScopeValue"
              @selected="chartDateScopeSelected"
            />
            <div v-if="chartDateScopeIsLastDays" class="chart-time-days">
              <Input
                class="chart-time-days-input"
                placeholder="Number of days"
                textType="number"
                validator="positiveInteger"
                :inputValue.sync="chartTimeDaysValue"
                @inputChanged="markTemplateCustom"
              />
            </div>
            <div v-if="chartDateScopeIsRange" class="chart-time-range">
              <DateTimePicker
                class="chart-time-picker"
                title="Start Date"
                :value.sync="chartTimeFromDateValue"
                :showTime="false"
                @update:value="markTemplateCustom"
              />
              <DateTimePicker
                class="chart-time-picker"
                title="End Date"
                :value.sync="chartTimeToDateValue"
                :showTime="false"
                @update:value="markTemplateCustom"
              />
            </div>
            <div v-if="chartValueModeList.length > 1">Value display</div>
            <Dropdown
              v-if="chartValueModeList.length > 1"
              placeholder="Value display"
              :items="chartValueModeList"
              itemKey="label"
              :selectedObject.sync="chartValueModeValue"
              @selected="markTemplateCustom"
            />
            <Dropdown
              v-if="chartGroupByList.length > 0 && chartTimeModeIsGrouped"
              placeholder="Time grouping"
              :items="chartGroupByList"
              itemKey="label"
              :selectedObject.sync="chartGroupByValue"
              @selected="markTemplateCustom"
            />
          </div>

          <hr v-if="chartFilterDefinition.length > 0" class="edit-divider" />
          <FiltersGroup
            v-if="chartFilterDefinition.length > 0"
            :value.sync="filterValue"
            :filterDefinition="chartFilterDefinition"
            @update:value="markTemplateCustom"
          />
        </div>

        <div v-if="widgetTypeEditValue.value == 'Buttons'">
          <div class="buttons-config field-config mb-2" v-if="buttonFieldsEdit.length > 0">
            <div
              class="buttons-config-item field-config-item"
              v-for="(item, index) in buttonFieldsEdit"
              :class="{ dragging: buttonDragIndex === index }"
              :key="index"
              draggable="true"
              @dragstart.stop="onButtonConfigItemDragStart(index)"
              @dragover.prevent.stop="onButtonConfigItemDragOver(index)"
              @dragend.stop="onButtonConfigItemDragEnd"
            >
              <div class="field-config-item-label">{{ item.label }}</div>
              <button type="button" class="field-config-item-delete" @click="buttonConfigDelete(index)" title="Remove action button">
                <img src="https://dsmlib.logistics.systems/Icons/icons8-minus-48.png" alt="" height="15" />
              </button>
            </div>
          </div>
          <Dropdown
            v-if="buttonFieldsEdit.length < 20"
            placeholder="Add action button"
            :items="editButtonList"
            @selected="editNewButtonSelected"
            :selectedObject.sync="editNewButtonValue"
            :search.sync="editNewButtonSearch"
            itemKey="label"
          />
          <div v-else style="color: darkred; font-size: 0.6rem; font-style: italic"
            >Max buttons added. Unable to add more</div
          >
        </div>

        <div v-if="widgetTypeEditValue.value == 'Stats'" class="edit-widget-fields d-flex flex-column stats-config">
          <div>Stats Fields</div>
          <div class="field-config stats-field-config">
            <div
              class="field-config-item stats-field-config-item"
              v-for="(col, index) in statsFieldsEdit"
              :key="col.id"
              :class="{ dragging: statsDragIndex === index }"
            >
              <div
                class="field-config-item-label"
                draggable="true"
                @dragstart.stop="onStatsConfigItemDragStart(index)"
                @dragover.prevent.stop="onStatsConfigItemDragOver(index)"
                @dragend.stop="onStatsConfigItemDragEnd"
                >{{ col.label }}</div
              >
              <button type="button" class="field-config-item-delete" @click="statsFieldDelete(index)" title="Remove statistic field">
                <img src="https://dsmlib.logistics.systems/Icons/icons8-minus-48.png" alt="" height="15" />
              </button>
            </div>
          </div>
          <Dropdown
            v-if="statsFieldsEdit.length < 20"
            placeholder="New Stat Field"
            :items="editStatsListFiltered"
            itemKey="label"
            @selected="editNewStatFieldSelected"
            :search.sync="editNewStatFieldInputValue"
          />
          <div v-else style="color: darkred; font-size: 0.6rem; font-style: italic"
            >Max stats added. Unable to add more</div
          >

          <hr v-if="statsFilterDefinition.length > 0" class="edit-divider" />
          <FiltersGroup
            v-if="statsFilterDefinition.length > 0"
            :value.sync="filterValue"
            :filterDefinition="statsFilterDefinition"
            @update:value="markTemplateCustom"
          />
        </div>

        <div
          v-if="widgetTypeEditValue.value == 'DailyReport'"
          class="edit-widget-fields d-flex flex-column daily-report-config"
        >
          <hr v-if="dailyReportFilterDefinition.length > 0" class="edit-divider" />
          <FiltersGroup
            v-if="dailyReportFilterDefinition.length > 0"
            :value.sync="filterValue"
            :filterDefinition="dailyReportFilterDefinition"
            @update:value="markTemplateCustom"
          />
          <div
            v-if="dailyReportFilterDefinition.length > 0"
            class="daily-report-default-filters"
            :title="dailyReportDefaultFilterTooltip"
          >
            <div class="daily-report-default-filters-title">Default behavior when unset:</div>
            <div
              v-for="filter in dailyReportDefaultFilters"
              :key="filter.key"
              class="daily-report-default-filter"
            >
              <span class="daily-report-default-filter-label">{{ filter.label }}:</span>
              <span>{{ filter.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <template v-if="widgetEditValue">
        <hr class="edit-divider" />
        <div class="widget-customization-group">
          <Input placeholder="Custom Title" :inputValue.sync="customTitleValue" />
        </div>
      </template>

      <div class="cta-group">
        <Button @click="saveEdit">Save</Button>
        <Button @click="cancelEdit">Cancel</Button>
      </div>
    </div>
    <!-- edit end -->

    <div v-else-if="isWidgetLoading" class="widget-loading">Widget is loading...</div>
    <div v-else-if="apiErrorMessage" class="dashboard-widget-error">{{ apiErrorMessage }}</div>
    <div v-else-if="widgetType == 'Buttons'" class="h-100">
      <div v-if="!widgetObject.buttonFields || widgetObject.buttonFields.length == 0" class="text-center mt-3 w-100">
        No buttons to show. Please add some.
      </div>
      <div class="buttons-list">
        <router-link
          :to="button.link"
          class="button-item"
          v-for="(button, index) in widgetObject.buttonFields"
          :key="index"
        >
          <div class="btn-img"><img :src="button.icon" alt="" /></div>
          <div class="btn-text-wrapper">
            <div class="btn-text">{{ button.label }}</div>
            <div class="btn-category">{{ button.moduleTitle }}</div>
          </div>
        </router-link>
      </div>
    </div>
    <div v-else-if="widgetType == 'Chart'" class="chart-container">
      <div
        class="chart-wrapper"
        :class="{
          'chart-wrapper-doughnut-tall': widgetObject.chartType == 'Doughnut' && !wideLayout && currentWidgetHeight == 2,
        }"
      >
        <DoughnutChart
          v-if="widgetObject.chartType == 'Doughnut'"
          :key="`doughnut-${chartDataVersion}`"
          v-bind="widgetObject.chartOptions"
          :legendPosition="!wideLayout && currentWidgetHeight == 2 ? 'bottom' : 'right'"
          :title="chartTitle"
          :labels="dataToChart.labels"
          :data="dataToChart.data"
        />
        <BarChart
          v-if="widgetObject.chartType == 'Bar'"
          :key="`bar-${chartDataVersion}`"
          :labels="dataToChart.labels"
          :data="dataToChart.data"
          :dataLabel="dataToChart.dataLabel"
          :title="chartTitle"
          :datasets="dataToChart.datasets"
        />
        <LineChart
          v-if="widgetObject.chartType == 'Line'"
          :key="`line-${chartDataVersion}`"
          :labels="dataToChart.labels"
          :data="dataToChart.data"
          :dataLabel="dataToChart.dataLabel"
          :title="chartTitle"
          :datasets="dataToChart.datasets"
        />
      </div>
    </div>

    <div class="details-list" v-else-if="widgetType == 'List'" ref="detailsList">
      <div class="details-item" v-for="(item, index) in detailsItems" :key="index">
        <div class="item-details">
          <div class="primary-fields-row">
            <div class="field-item jobno-text">
              <div class="field-item-label">{{
                widgetListDetails.primaryLeftField ? widgetListDetails.primaryLeftField.label : ''
              }}</div>
              <div
                ><b>{{
                  item[widgetListDetails.primaryLeftField ? widgetListDetails.primaryLeftField.key : '']
                }}</b></div
              >
            </div>
            <div class="field-item jobdate-text" v-if="widgetListDetails.primaryRightField">
              <div class="field-item-label">{{ widgetListDetails.primaryRightField.label }}</div>
              <div v-if="item[widgetListDetails.primaryRightField.key]">{{
                item[widgetListDetails.primaryRightField.key]
              }}</div>
              <div class="no-data" v-else>No data</div>
            </div>
            <div
              class="item-actions context-menu-wrapper"
              :ref="'contextMenu-' + index"
              :class="{ active: isDetailsMenuOpen(index) }"
              v-if="hasListActions"
            >
              <Button class="menu-btn" @click.stop="toggleDetailsMenuOpen(index)"
                ><img src="https://dsmlib.logistics.systems/Icons/icons8-ellipsis-100.png" alt="" height="12"
              /></Button>

              <div class="context-menu-list">
                <div
                  v-for="(action, actionIndex) in widgetObject.fieldsActions"
                  :key="actionIndex"
                  class="context-menu-item"
                  @click="action.clickFunction(getListActionItem(item))"
                >
                  {{ action.label }}
                </div>
              </div>
            </div>
          </div>
          <div
            class="details-fields"
            :class="{ open: isDetailsFieldsOpen(index) }"
            v-if="widgetListDetails.detailsFields.length > 0"
          >
            <div class="details-fields-inner">
              <div class="details-fields-list">
                <div
                  class="field-item"
                  v-for="(detailsItem, detailsIndex) in widgetListDetails.detailsFields"
                  :key="detailsIndex"
                  :style="{ gridColumn: `span ${detailsItem && detailsItem.span ? detailsItem.span : '1'}` }"
                >
                  <div class="field-item-label">{{ detailsItem.label }}</div>

                  <div class="field-item-text" v-if="item[detailsItem.key]">{{ item[detailsItem.key] }}</div>
                  <div v-else class="field-item-text no-data">No data</div>
                </div>
              </div>
              <div class="item-actions" v-if="detailsDetailsFieldBtnEnable">
                <Button class="menu-btn" @click="toggleDetailsFieldsOpen(index)"
                  ><img src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png" alt="" height="12"
                /></Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="small text-center py-2"
        >Showing first {{ detailsItems.length }} sorted by {{ sortKeyLabel }} ({{
          widgetObject.sortDesc ? 'Desc' : 'Asc'
        }})
      </div>
    </div>

    <div class="search-widget" v-else-if="widgetType == 'Search'">
      <div v-if="searchLoading" class="text-center my-3">Searching...</div>
      <div class="search-list-container" v-else-if="searchResultList" ref="searchList">
        <div class="search-row">
          <div class="search-row-wrapper">
            <input placeholder="Enter Code Here" v-model="searchValue" @keyup.enter="dashboardSearch" />
            <Button @click="dashboardSearch">Search</Button>
          </div>
          <div v-if="searchValueError" style="color: red">{{ searchValueError }}</div>
        </div>

        <div class="search-list">
          <div
            class="search-item"
            v-for="(search, index) in searchResultsFiltered"
            :key="index"
            :style="{ order: checkPrimaryHighLight(search.primaryValue) ? '-1' : '0' }"
          >
            <div class="search-item-primary-row">
              <div class="field-item mr-auto">
                <div class="field-item-label">{{ search.primaryKey }}</div>
                <div class="field-item-text" v-html="highlightText(search.primaryValue)"></div>
              </div>
              <div class="field-item" style="min-width: 20%">
                <div class="field-item-label">Category</div>
                <div class="field-item-text">{{ search.category }}</div>
              </div>
              <div class="action-buttons-list">
                <div
                  v-for="(action, actionIndex) in search.actions"
                  :key="actionIndex"
                  class="action-buttons"
                  @click="action.clickFunction(search)"
                >
                  {{ action.label }}
                </div>
              </div>
            </div>
            <div class="search-item-details">
              <div class="search-item-details-item" v-for="(val, key) in search.details" :key="key">
                <div class="field-item">
                  <div class="field-item-label">{{ key }}</div>
                  <div class="field-item-text" v-if="val" v-html="highlightText(val)"></div>
                  <div class="field-item-text no-data" v-else>No data</div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="searchResultsFiltered.length > 0" class="text-center my-3">
            Showing {{ searchResultsFiltered.length }} results for {{ activeSearchValue }}
          </div>
          <div v-else class="text-center my-3"> No results </div>
        </div>
      </div>
      <div class="search-initial" v-else>
        <div class="search-img"><img :src="widgetIcon" alt="" /></div>
        <div class="search-text">Enter any Code to start searching</div>
        <input placeholder="Enter Code Here" v-model="searchValue" @keyup.enter="dashboardSearch" />

        <div v-if="searchValueError" style="color: red; font-size: 0.7rem">{{ searchValueError }}</div>
        <Button @click="dashboardSearch">Search</Button>
      </div>
    </div>

    <div v-else-if="widgetType == 'Notifications'">
      <div class="notification-list">
        <div
          class="notification-item"
          v-for="noti in notificationsListFiltered"
          :key="noti.NotifID"
          :class="[noti.Severity, { 'is-unread': !noti.IsRead }]"
        >
          <div class="notification-title-row">
            <div class="notification-item-title">
              <div class="mr-auto">{{ noti.ModuleCode }}</div>
              <div v-if="!noti.IsRead" class="unread-badge">New</div>
            </div>
            <div>{{ noti.NotifDateTime ? new Date(noti.NotifDateTime).formatString('yyyy-MM-dd HH:mm') : '' }}</div>
          </div>
          <div class="notification-details">
            <img :src="noti.icon" alt="" height="24" />

            <div>
              <div class="color-dark font-weight-bold mb-1">{{ noti.Title }}</div>
              <div>{{ noti.Description }}</div>
            </div>
            <div class="actions">
              <span
                class="action-button"
                title="Actions"
                v-if="noti.link"
                @click="updateNotificationIsRead(noti.NotifID, '1')"
              >
                <router-link :to="noti.link">
                  <img src="https://dsmlib.logistics.systems/Icons/open-in-new-tab.png" alt="" width="20" />
                </router-link>
              </span>
              <span
                class="action-button"
                title="Mark as Read"
                v-if="!noti.IsRead"
                @click="updateNotificationIsRead(noti.NotifID, '1')"
                ><img src="https://dsmlib.logistics.systems/Icons/open-mail.png" alt="Mark as Read" width="18"
              /></span>
              <span
                class="action-button"
                title="Mark as Unread"
                v-else
                @click="updateNotificationIsRead(noti.NotifID, '0')"
                ><img src="https://dsmlib.logistics.systems/Icons/unopened-email.png" alt="Mark as Unread" width="18"
              /></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="widgetType == 'Stats'" class="h-100">
      <div class="stats-container">
        <div class="stats-item" v-for="(statItem, index) in widgetObject.statsToShow" :key="index">
          <div
            class="stats-number"
            v-if="statsItems[statItem.key]"
            v-html="formatAppends(statsItems[statItem.key])"
          ></div>
          <div class="stats-number no-data" v-else>No data</div>
          <div class="stats-label">{{ statItem.label }}</div>
        </div>
      </div>
    </div>

    <div v-else-if="widgetType == 'DailyReport'" class="daily-report-widget-shell">
      <DailyReport :items="dailyReportItems" @statItemClicked="openDailyReportJobs" />
      <div class="daily-report-widget-footer">
        <button class="daily-report-popup-button" @click="openDailyReportJobs(null)">Open Jobs</button>
      </div>
    </div>

      <DailyReportPopup
        :show.sync="showDailyReportPopup"
        :selectedItem="selectedDailyReportItem"
        :payload="dailyReportPopupPayload"
        :items="dailyReportItems"
      />
    </template>
  </div>
</template>

<script>
import DoughnutChart from './Charts/DoughnutChart.vue';
import BarChart from './Charts/BarChart.vue';
import LineChart from './Charts/LineChart.vue';
import DailyReport from './DailyReport.vue';
import DailyReportPopup from './DailyReportPopup.vue';
import FiltersGroup from '../FiltersGroup.vue';
import { dashboardWidgetTemplates } from './dashboardItemConfig';

const dashboardOptionsMetadataCache = Object.create(null);
const dashboardOptionsRequestCache = Object.create(null);

export default {
  components: {
    DoughnutChart,
    BarChart,
    LineChart,
    DailyReport,
    DailyReportPopup,
    FiltersGroup,
  },
  computed: {
    widgetObject() {
      if (!this.widget) return this.widget;
      let returnWidget = { ...this.widget };

      const { widgetType, key: widgetKey, buttonFields } = returnWidget;
      const dashboardItem = this.getWidgetConfig(widgetKey ?? this.widgetEditValue?.key ?? '');
      if (dashboardItem) returnWidget = { ...returnWidget, ...dashboardItem };

      if (widgetType == 'List') {
        const { detailsFields, primaryLeftField, primaryRightField } = returnWidget;
        const findFields = detailsFields
          ?.map((item) => dashboardItem?.fields.find((field) => field.key == item))
          .filter((item) => item);
        returnWidget = {
          ...returnWidget,
          detailsFields: findFields,
          primaryLeftField: dashboardItem?.fields.find((item) => item.key == primaryLeftField),
          primaryRightField: dashboardItem?.fields.find((item) => item.key == primaryRightField ?? null),
        };
      } else if (widgetType == 'Chart') {
        returnWidget.chartType = this.normalizeDashboardChartType(returnWidget.chartType);
        const { dataToShow, chartType, chartGroup } = returnWidget;
        const chartGroupDefinition = dashboardItem?.chartGroups?.find((item) => item.key == chartGroup);
        if (chartGroupDefinition) {
          const firstSeries = chartGroupDefinition.series?.[0];
          returnWidget.chartGroupDefinition = chartGroupDefinition;
          returnWidget.dataToShow = dashboardItem?.data?.find((item) => item.key == dataToShow) || firstSeries;
          returnWidget.chartType = this.getChartGroupChartType(chartGroupDefinition, returnWidget.chartType);
          returnWidget.chartOptions = {};
        } else {
          returnWidget.dataToShow = dashboardItem?.data?.find((item) => item.key == dataToShow);
          const typeConfig = returnWidget.dataToShow?.type?.find((t) => t.key == chartType);
          returnWidget.chartOptions = typeConfig?.options ?? {};
        }
      } else if (widgetType == 'Buttons') {
        const icons = this.subMenuIcons;
        returnWidget.buttonFields = buttonFields
          ?.map((item) => {
            const buttonDetails = dashboardItem?.buttons?.find((field) => field.key == item);
            if (!buttonDetails) return null;
            const findCategory = icons.find(
              (i) =>
                buttonDetails.link
                  .split('/')
                  .filter((item) => item)[0]
                  .toLowerCase() == i.module.toLowerCase(),
            );
            return {
              ...buttonDetails,
              link: `/Home/${this.userParam}${buttonDetails.link}`,
              icon: findCategory?.icon ?? '',
              moduleTitle: findCategory?.title ?? '',
            };
          })
          .filter((item) => item);
      } else if (widgetType == 'Stats') {
        const { statsToShow } = returnWidget;
        returnWidget.statsToShow =
          statsToShow?.map((item) => dashboardItem?.stats.find((stat) => stat.key == item)).filter((i) => i) ?? [];
      }

      return returnWidget;
    },

    widgetObjectEmpty() {
      return !this.widgetObject || this.widgetObject.new;
    },
    widgetIcon() {
      if (this.widgetObjectEmpty) return null;
      if (this.widgetObject.icon) return this.widgetObject.icon;
      if (this.widgetObject.link) return this.getIcon(this.widgetObject.link);
      return null;
    },
    widgetTitle() {
      if (this.widgetObjectEmpty) return 'Add Widget';
      if (this.editMode) return 'Edit Widget';
      if (this.widgetObject?.customTitle) return this.escapeHtml(this.widgetObject.customTitle);
      let title = this.widgetObject?.title ?? 'Title';
      const { widgetType } = this.widgetObject;
      if (widgetType == 'Chart') {
        const findChartType = this.chartEditTypeList.find((item) => item.key == this.widgetObject.chartType);
        const chartLabel = this.widgetObject.chartGroupDefinition?.label || this.widgetObject.dataToShow?.label || '';
        title += ` ${findChartType?.label ?? ''} Chart for ${chartLabel}`;
      } else if (widgetType == 'List') {
        title += ' List ';
      } else if (widgetType == 'Stats') {
        title += ' Stats';
      } else if (widgetType == 'DailyReport') {
        return 'Import Job Customs Status Report';
      }
      return title;
    },
    chartTitle() {
      if (this.widgetObjectEmpty || this.widgetObject?.widgetType != 'Chart') return '';

      const tableTitle = this.widgetObject?.title || '';
      const chartLabel = this.widgetObject.chartGroupDefinition?.label || this.widgetObject.dataToShow?.label || '';
      const hasJobDateFilter = Array.isArray(this.widgetObject.filtersArr) &&
        this.widgetObject.filtersArr.some((filter) => filter?.key == 'jobdatetime');
      const isDateBasedGroup = this.widgetObject.chartGroupDefinition?.defaultDateScope == 'six_months';
      const dateLabel = hasJobDateFilter || isDateBasedGroup ? ' by Job Date' : '';

      return `${tableTitle}${chartLabel ? ` - ${chartLabel}` : ''}${dateLabel}`;
    },
    activeWidgetFilters() {
      const filters = this.widgetObject?.filtersArr || [];
      const chartTimeOwnsJobDate = this.widgetObject?.widgetType == 'Chart' &&
        this.widgetObject?.chartGroup && this.widgetObject?.chartTime;
      return chartTimeOwnsJobDate ? filters.filter((filter) => filter?.key != 'jobdatetime') : filters;
    },
    activeFilterCount() {
      return this.activeWidgetFilters.length;
    },
    activeFilterText() {
      return `${this.activeFilterCount} filter${this.activeFilterCount == 1 ? '' : 's'} active`;
    },
    dailyReportDefaultFilters() {
      return [
        { key: 'reportdate', label: 'Cutoff Date', value: 'Last 6 months to today' },
        { key: 'has1253a', label: 'Include Not Sent To Operations (1253A)?', value: 'No' },
        { key: 'includereleasedbeforecutoff', label: 'Include Releases More Than 2 Days Before Cutoff?', value: 'No' },
      ];
    },
    dailyReportDefaultFilterTooltip() {
      return [
        'These defaults apply when the corresponding report filter is not set:',
        ...this.dailyReportDefaultFilters.map((filter) => `${filter.label}: ${filter.value}`),
      ].join('\n');
    },
    activeFilterTooltip() {
      return this.activeWidgetFilters
        .map((filter) => {
          const definition = this.widgetFilterDefinitions.find((item) => item.key == filter.key);
          return `${definition?.label || filter.key}: ${this.getFilterDisplayValue(filter, definition)}`;
        })
        .join('\n');
    },
    widgetFilterDefinitions() {
      const chartFilters = this.widgetObject?.chartGroupDefinition
        ? this.getChartGroupFilterDefinitions(
            this.widgetObject.chartGroupDefinition,
            this.widgetObject.chartFilter,
            Boolean(this.widgetObject.chartTime),
          )
        : this.widgetObject?.chartFilter || [];
      return [
        ...(this.widgetObject?.listFilter || []),
        ...chartFilters,
        ...(this.widgetObject?.statsFilter || []),
        ...(this.widgetObject?.dailyReportFilter || []),
      ];
    },
    widgetLink() {
      return this.widgetObject?.link ? `/Home/${this.userParam}${this.widgetObject.link}` : '';
    },

    widgetType() {
      return this.widgetObject?.widgetType ?? '';
    },

    widgetGridStyle() {
      return {
        gridRow: `span ${this.currentWidgetHeight}`,
        gridColumn: `span ${this.currentWidgetWidth}`,
      };
    },

    widgetListFiltered() {
      return this.widgetList
        ?.filter((item) => !item.disabled)
        .filter(
          (item) =>
            !this.dashboardSasEnabled ||
            item.skipSAS ||
            this.dashboardAccess.includes(`${item.controlKey}DisableNew`) ||
            this.dashboardAccess.includes(item.controlKey),
        )
        .map((item) => this.getWidgetConfig(item.key) || item);
    },

    widgetListDetails() {
      const { primaryLeftField, primaryRightField, detailsFields } = this.widgetObject;
      return {
        primaryLeftField,
        primaryRightField,
        detailsFields,
      };
    },

    hasListActions() {
      return this.widgetObject?.fieldsActions?.length > 0;
    },

    widgetTypeListFiltered() {
      const { type, controlKey } = this.widgetEditValue || {};
      return (type || [])
        .map((item) => this.widgetTypeList.find((i) => i.value == item))
        .filter((item) => item)
        .filter(
        (item) =>
          !this.dashboardSasEnabled ||
          this.widgetEditValue?.skipSAS ||
          this.dashboardAccess.includes(`${controlKey}${item.value}`) ||
          this.dashboardAccess.includes(controlKey) ||
          ['Search', 'Buttons'].includes(item.value),
        );
    },

    showWidgetTypeDropdown() {
      const hiddenSingleTypes = ['Search', 'Buttons'];
      return (
        this.widgetEditValue &&
        this.widgetTypeListFiltered.length > 0 &&
        !(this.widgetTypeListFiltered.length == 1 && hiddenSingleTypes.includes(this.widgetTypeListFiltered[0]?.value))
      );
    },

    widgetTemplateList() {
      const widgetType = this.widgetTypeEditValue?.value;
      return dashboardWidgetTemplates.filter((template) => template.widgetTypes.includes(widgetType));
    },

    chartTimeFromDateValue: {
      get() {
        return this.parseDateOnlyValue(this.chartTimeFromValue);
      },
      set(value) {
        this.chartTimeFromValue = value ? this.getDateOnlyValue(value) : '';
      },
    },

    chartTimeToDateValue: {
      get() {
        return this.parseDateOnlyValue(this.chartTimeToValue);
      },
      set(value) {
        this.chartTimeToValue = value ? this.getDateOnlyValue(value) : '';
      },
    },

    canResizeWidget() {
      const key = this.widgetEditValue?.key ?? this.widgetObject?.key;
      return this.widgetObjectEmpty ? Boolean(this.widget?.id) : Boolean(key);
    },

    currentWidgetHeight() {
      return this.editMode || this.widgetObjectEmpty ?
          this.normalizeWidgetSpan(this.widgetHeightValue?.value)
        : this.normalizeWidgetSpan(this.widgetObject?.rowSpan);
    },

    currentWidgetWidth() {
      return this.editMode || this.widgetObjectEmpty ?
          this.normalizeWidgetSpan(this.widgetWidthValue?.value)
        : this.normalizeWidgetSpan(this.widgetObject?.colSpan);
    },

    editFieldsList() {
      const { fields } = this.widgetEditValue || {};
      return fields ?? [];
    },

    sortKeyLabel() {
      return (
        this.widgetObject?.fields?.find((item) => item.key == this.widgetObject.sortKey)?.label ??
        this.widgetObject?.sortFields?.find((item) => item.key == this.widgetObject.sortKey)?.label ??
        ''
      );
    },

    listFilterDefinition() {
      return this.widgetEditValue?.listFilter ?? [];
    },

    statsFilterDefinition() {
      return this.widgetEditValue?.statsFilter ?? [];
    },
    dailyReportFilterDefinition() {
      return this.widgetEditValue?.dailyReportFilter ?? [];
    },
    dailyReportPopupPayload() {
      return this.getDailyReportPayload(this.normalizeDashboardFiltersForApi(this.widgetObject?.filtersArr || []));
    },
    chartFilterDefinition() {
      if (this.chartGroupValue) {
        return this.getChartGroupFilterDefinitions(this.chartGroupValue, this.widgetEditValue?.chartFilter, true);
      }
      return this.widgetEditValue?.chartFilter ?? [];
    },

    editFieldsPrimaryListFiltered() {
      return this.editFieldsList.filter((item) => item.primary);
    },

    editFieldsListFiltered() {
      const existingKeys = [
        ...(this.detailFieldsEdit?.map((i) => i.key) || []),
        this.primaryLeftFieldValue?.key,
        this.primaryRightFieldValue?.key,
      ].filter((i) => i);
      return this.editFieldsList.filter((i) => !existingKeys.includes(i.key));
    },

    editFieldsLeftListFiltered() {
      const existingKeys = [
        ...(this.detailFieldsEdit?.map((i) => i.key) || []),
        this.primaryRightFieldValue?.key,
      ].filter((i) => i);
      return this.editFieldsList.filter((i) => !existingKeys.includes(i.key));
    },

    editFieldsListSort() {
      return this.widgetEditValue?.sortFields ?? this.editFieldsList.filter((item) => item.sort);
    },

    detailsDetailsFieldBtnEnable() {
      const detailsFields = this.widgetListDetails?.detailsFields || [];
      const span = detailsFields.reduce((acc, cur) => acc + (Number(cur?.span) || 1), 0);
      const visibleUnits = this.wideLayout ? 6 : 3;
      return span > visibleUnits;
    },

    chartGroupList() {
      return this.widgetEditValue?.chartGroups ?? [];
    },

    chartWidgetTypeListFiltered() {
      if (this.chartGroupValue) {
        return this.chartEditTypeList.filter((item) => this.chartGroupValue.chartTypes?.includes(item.key));
      }
      const types = this.dataToShowValue?.type ?? [];
      return this.chartEditTypeList.filter((item) => types.some((t) => t.key == item.key)) ?? [];
    },

    chartDateScopeList() {
      const keys = this.chartGroupValue?.dateScopes || this.getLegacyChartDateScopes(this.chartGroupValue);
      return this.chartDateScopeOptions.filter((item) => keys.includes(item.key));
    },

    chartValueModeList() {
      const keys = this.chartGroupValue?.valueModes || ['period', 'cumulative'];
      return this.chartValueModeOptions.filter((item) => keys.includes(item.key));
    },

    chartDateScopeIsLastDays() {
      return this.chartDateScopeValue?.key == 'last_days';
    },

    chartDateScopeIsRange() {
      return this.chartDateScopeValue?.key == 'date_range';
    },

    chartGroupByList() {
      const keys = this.chartGroupValue?.groupBy || [];
      return this.chartGroupByOptions.filter((item) => keys.includes(item.key));
    },

    chartTimeModeIsGrouped() {
      return this.chartDateScopeValue?.key != 'all';
    },

    subMenuIcons() {
      const subMenus = this.$subMenu.getMenus();
      return subMenus.map((item) => ({ module: item.module, icon: item.icon, title: item.title }));
    },

    editButtonList() {
      return (
        this.widgetEditValue?.buttons
          .filter((item) => !this.buttonFieldsEdit.find((edit) => edit.key == item.key))
          .filter((item) => !this.dashboardSasEnabled || this.dashboardAccess.includes(`${item.controlKey}`))
          .map((item) => {
            const { link: url } = item;
            const getFirstSplit = url.split('/').filter((item) => item)[0];
            const findMenu = this.subMenuIcons.find((item) => item.module.toLowerCase() == getFirstSplit.toLowerCase());
            return {
              ...item,
              label: `[${findMenu?.title ?? ''}] ${item.label}`,
            };
          }) ?? []
      );
    },

    searchResultsFiltered() {
      const { table } = this.widgetObject || {};
      const flatResults =
        table?.flatMap((t) =>
          (this.searchResultList[t.key] || []).map((item) => {
            const keys = Object.keys(item);
            const primaryKey = keys[0];
            const primaryValue = item[primaryKey];
            const details = Object.fromEntries(keys.slice(1).map((k) => [k, item[k]]));

            return {
              category: t.label,
              tablekey: t.key,
              primaryKey: primaryKey,
              primaryValue: primaryValue,
              link: `/Home/${this.userParam}${t.link}`,
              details,
              actions: t.actions ?? [],
            };
          }),
        ) ?? [];

      return flatResults;
    },

    editStatsListFiltered() {
      const existingKeys = this.statsFieldsEdit.map((i) => i.key);
      return (
        this.widgetEditValue?.stats
          ?.filter((i) => !existingKeys.includes(i.key))
          .map((item) => ({ ...item })) ?? []
      );
    },

    notificationsListFiltered() {
      const { moduleMapping } = this.widgetObject;

      return this.notificationsList.map((item) => {
        const { ModuleCode, RefNo } = item;
        const findModule = moduleMapping?.find((moduleItem) => moduleItem.key == ModuleCode);
        const routePath = findModule?.link ?? null;

        const findMenuIcon =
          findModule ?
            this.subMenuIcons.find((i) => i.module.toLowerCase() == routePath?.split('/')[1].toLowerCase())
          : null;

        return {
          ...item,
          link: routePath ? `/Home/${this.userParam}${routePath}?Edit=${RefNo}` : null,
          icon: findMenuIcon?.icon ?? 'https://dsmlib.logistics.systems/Icons/Help.png',
        };
      });

    },
  },
  data: function () {
    return {
      contextMenuIsOpen: false,

      isWidgetLoading: false,
      skipNextWidgetDataReload: false,
      dashboardOptionsMetadata: {},
      dashboardOptionsMigratedKey: '',
      dataLoadRequestId: 0,

      editMode: false,
      userParam: '',

      widgetTypeList: [
        { label: 'List', value: 'List' },
        { label: 'Chart', value: 'Chart' },
        { label: 'Statistics', value: 'Stats' },
        { label: 'Import Job Customs Status Report', value: 'DailyReport' },
        { label: 'Buttons', value: 'Buttons' },
        { label: 'Search', value: 'Search' },
        { label: 'Notifications', value: 'Notifications' },
      ],

      widgetEditValue: null,
      widgetEditValueError: '',
      widgetTypeEditValue: null,
      widgetTypeEditValueError: '',
      widgetSizeOptions: [
        { label: '1 Cell', value: 1 },
        { label: '2 Cells', value: 2 },
      ],
      widgetHeightValue: { value: 1 },
      widgetWidthValue: { value: 1 },
      widgetTemplateValue: null,

      detailsMenuOpenItems: [],
      detailsFieldsOpenItems: [],

      detailFieldsEdit: [],
      editNewFieldInputValue: '',
      customTitleValue: '',

      dragIndex: null,
      primaryLeftFieldValue: null,
      primaryLeftFieldValueError: '',
      primaryRightFieldValue: null,

      sortByValue: null,
      sortByValueError: '',
      sortByDescValue: true,

      maxRowsValue: '10',

      detailsItems: [],

      chartEditTypeList: [
        {
          label: 'Line',
          key: 'Line',
        },
        {
          label: 'Bar',
          key: 'Bar',
        },
        { label: 'Doughnut', key: 'Doughnut' },
      ],

      dataToShowValue: null,
      chartGroupValue: null,
      chartGroupValueError: '',
      chartTypeValue: null,
      chartTypeValueError: '',
      chartDateScopeOptions: [
        { label: 'Today', key: 'today' },
        { label: 'Last N days', key: 'last_days' },
        { label: 'This month', key: 'this_month' },
        { label: 'This year', key: 'this_year' },
        { label: 'Date range', key: 'date_range' },
        { label: 'All time', key: 'all' },
      ],
      chartValueModeOptions: [
        { label: 'Values per period', key: 'period' },
        { label: 'Cumulative values', key: 'cumulative' },
      ],
      chartGroupByOptions: [
        { label: 'Hour', key: 'hour' },
        { label: 'Day', key: 'day' },
        { label: 'Week', key: 'week' },
        { label: 'Month', key: 'month' },
      ],
      chartDateScopeValue: null,
      chartValueModeValue: null,
      chartGroupByValue: null,
      chartTimeDaysValue: '7',
      chartTimeFromValue: '',
      chartTimeToValue: '',

      dataToChart: {
        labels: [],
        data: [],
        datasets: [],
        dataLabel: '',
      },
      chartDataVersion: 0,

      buttonFieldsEdit: [],
      editNewButtonValue: null,
      editNewButtonSearch: '',

      buttonDragIndex: null,
      searchValue: '',
      activeSearchValue: '',
      searchResultList: '',
      searchValueError: '',
      searchLoading: false,

      statsFieldsEdit: [],
      editNewStatFieldInputValue: '',
      statsDragIndex: null,
      statsItems: [],

      dailyReportItems: [],
      showDailyReportPopup: false,
      selectedDailyReportItem: null,

      hasError: false,
      apiErrorMessage: '',

      notificationsList: [],

      filterValue: [],
    };
  },
  props: {
    widget: { type: Object, default: () => ({}) },
    widgetList: { type: Array, default: () => [] },
    dashboardAccess: { type: Array, default: () => [] },
    dashboardSasEnabled: { type: Boolean, default: true },
    wideLayout: { type: Boolean, default: false },
  },
  methods: {
    normalizeDashboardChartType(chartType) {
      const value = typeof chartType == 'object' ? chartType?.key : chartType;
      return value == 'Pie' ? 'Doughnut' : value;
    },

    getChartGroupChartType(group, chartType) {
      chartType = this.normalizeDashboardChartType(chartType);
      const chartTypes = (group?.chartTypes?.length ? group.chartTypes : [group?.chartType || 'Line'])
        .map((type) => this.normalizeDashboardChartType(type));
      const defaultChartType = this.normalizeDashboardChartType(group?.chartType);
      if (chartTypes.includes(chartType)) return chartType;
      if (defaultChartType && chartTypes.includes(defaultChartType)) return defaultChartType;
      return chartTypes[0] || 'Line';
    },

    getChartGroupFilterDefinitions(group, fallbackDefinitions = [], excludeJobDate = true) {
      return (group?.filters || [])
        .map((filter) =>
          typeof filter == 'string' ? fallbackDefinitions.find((definition) => definition.key == filter) : filter,
        )
        .filter((filter) => filter?.key && (!excludeJobDate || filter.key != 'jobdatetime'));
    },

    getChartGroupFilterKeys(group) {
      return new Set(
        (group?.filters || [])
          .map((filter) => (typeof filter == 'string' ? filter : filter?.key))
          .filter((key) => key && key != 'jobdatetime'),
      );
    },

    getLegacyChartMapping(config, dataToShow, chartType) {
      const mappings = config?.legacyChartMappings || [];
      return mappings.find((mapping) =>
        mapping.dataToShow == dataToShow && mapping.chartTypes?.includes(chartType),
      ) || mappings.find((mapping) => mapping.dataToShow == dataToShow && !mapping.chartTypes) || null;
    },

    getChartGroupCompatibleDataKey(config, group) {
      const mapping = (config?.legacyChartMappings || []).find((item) => item.group == group?.key);
      return mapping?.dataToShow || group?.series?.[0]?.key || null;
    },

    getWidgetConfig(key) {
      const dashboardItem = this.widgetList.find((item) => item.key == key);
      if (!dashboardItem) return null;
      return this.applyDashboardOptionsMetadata(dashboardItem, this.dashboardOptionsMetadata[key]);
    },

    normalizeDashboardMetadataOptions(options = []) {
      if (!Array.isArray(options)) return [];
      return options
        .map((option) => {
          const value = option?.value ?? option?.key;
          if (value === null || value === undefined || value === '') return null;
          return {
            ...option,
            key: option.key ?? value,
            value,
            label: option.label ?? value,
          };
        })
        .filter((option) => option);
    },

    normalizeDashboardMetadataFilters(filters = []) {
      if (!Array.isArray(filters)) return [];
      return filters
        .map((filter) => {
          if (!filter?.key || !filter?.filterType) return null;
          const options = this.normalizeDashboardMetadataOptions(filter.options);
          return {
            ...filter,
            options,
            saveKey: options.length > 0 ? 'value' : filter.saveKey,
          };
        })
        .filter((filter) => filter);
    },

    normalizeDashboardMetadataSorts(sorts = []) {
      if (!Array.isArray(sorts)) return [];
      return sorts
        .map((sort) => {
          if (!sort?.key) return null;
          return {
            ...sort,
            label: sort.label || sort.key,
            defaultDesc: sort.defaultDesc === true,
          };
        })
        .filter((sort) => sort);
    },

    normalizeDashboardMetadataColumns(columns = []) {
      if (!Array.isArray(columns)) return [];
      return columns
        .map((column) => {
          if (!column?.key) return null;
          return {
            ...column,
            label: column.label || column.key,
            primary: column.primary === true || column.Primary === true,
          };
        })
        .filter((column) => column);
    },

    normalizeDashboardMetadataChartColumns(columns = []) {
      if (!Array.isArray(columns)) return [];
      return columns
        .map((column) => {
          if (!column?.key) return null;
          const type = Array.isArray(column.type) ? column.type : [{ key: 'Bar' }];
          return {
            ...column,
            label: column.label || column.key,
            type,
          };
        })
        .filter((column) => column);
    },

    normalizeDashboardMetadataChartGroups(groups = []) {
      if (!Array.isArray(groups)) return [];
      return groups
        .map((group) => {
          if (!group?.key) return null;
          const series = Array.isArray(group.series || group.Series) ? group.series || group.Series : [];
          const chartType = this.normalizeDashboardChartType(group.chartType || group.ChartType || 'Line');
          const configuredChartTypes = Array.isArray(group.chartTypes || group.ChartTypes) ?
              group.chartTypes || group.ChartTypes
            : [chartType];
          const supportedChartTypes = configuredChartTypes
            .map((type) => (typeof type == 'string' ? type : type?.key))
            .map((type) => this.normalizeDashboardChartType(type))
            .filter((type) => this.chartEditTypeList.some((item) => item.key == type));
          const chartTypes = supportedChartTypes.length > 0 ? supportedChartTypes : ['Line'];
          const normalizeKeys = (value) =>
            (Array.isArray(value) ? value : [])
              .map((item) => (typeof item == 'string' ? item : item?.key ?? item?.value))
              .filter((item) => item);
          const timeModes = normalizeKeys(group.timeModes || group.TimeModes);
          const timeWindows = normalizeKeys(group.timeWindows || group.TimeWindows);
          const dateScopes = normalizeKeys(group.dateScopes || group.DateScopes);
          const valueModes = normalizeKeys(group.valueModes || group.ValueModes);
          const groupBy = normalizeKeys(group.groupBy || group.GroupBy);
          return {
            ...group,
            label: group.label || group.key,
            chartType: chartTypes.includes(chartType) ? chartType : chartTypes[0],
            chartTypes,
            series: series
              .map((item) => {
                if (!item?.key) return null;
                const precision = parseInt(item.precision ?? item.Precision, 10);
                return {
                  ...item,
                  label: item.label || item.key,
                  unit: item.unit || item.Unit || 'jobs',
                  precision: Number.isNaN(precision) ? 0 : Math.max(0, precision),
                };
              })
              .filter((item) => item),
            filters: this.normalizeDashboardMetadataFilters(group.filters || group.Filters || []),
            defaultDateScope: group.defaultDateScope || group.DefaultDateScope || 'six_months',
            dateScopes: dateScopes.length > 0 ? dateScopes : this.getLegacyChartDateScopes({ chartTypes }),
            defaultScope: group.defaultScope || group.DefaultScope ||
              ((group.DefaultDateScope == 'none' || group.defaultDateScope == 'none') ? 'all' : 'last_days'),
            defaultDays: parseInt(group.defaultDays ?? group.DefaultDays, 10) || 180,
            valueModes: valueModes.length > 0 ? valueModes : (chartTypes.includes('Doughnut') ? ['period'] : ['period', 'cumulative']),
            defaultValueMode: group.defaultValueMode || group.DefaultValueMode || 'period',
            timeModes: timeModes.length > 0 ? timeModes : ['window', 'cumulative_since', 'snapshot'],
            timeWindows: timeWindows.length > 0 ? timeWindows : [
              'today',
              'yesterday',
              'last_7_days',
              'last_30_days',
              'this_month',
              'last_6_months',
              'ytd',
              'custom',
              'all',
            ],
            groupBy,
            defaultTimeMode: group.defaultTimeMode || group.DefaultTimeMode || 'window',
            defaultTimeWindow: group.defaultTimeWindow || group.DefaultTimeWindow ||
              (group.DefaultDateScope == 'none' || group.defaultDateScope == 'none' ? 'all' : 'last_6_months'),
            defaultGroupBy: group.defaultGroupBy || group.DefaultGroupBy || 'month',
          };
        })
        .filter((group) => group && group.series.length > 0);
    },

    applyDashboardOptionsMetadata(dashboardItem, metadata) {
      if (!metadata || metadata.Results == '1') return dashboardItem;
      const columns = this.normalizeDashboardMetadataColumns(metadata.columns || metadata.Columns);
      const filters = metadata.filters || metadata.Filters;
      const listFilters = this.normalizeDashboardMetadataFilters(metadata.listFilters || metadata.ListFilters || filters);
      const chartFilters = this.normalizeDashboardMetadataFilters(
        metadata.chartFilters || metadata.ChartFilters || filters,
      );
      const statsFilters = this.normalizeDashboardMetadataFilters(
        metadata.statsFilters || metadata.StatsFilters || filters,
      );
      const sorts = this.normalizeDashboardMetadataSorts(metadata.sorts || metadata.Sorts);
      const chartColumns = this.normalizeDashboardMetadataChartColumns(metadata.chartColumns || metadata.ChartColumns);
      const rawChartGroups = metadata.chartGroups || metadata.ChartGroups;
      const chartGroups = Array.isArray(rawChartGroups)
        ? this.normalizeDashboardMetadataChartGroups(rawChartGroups)
        : dashboardItem.chartGroups || [];
      const statsColumns = this.normalizeDashboardMetadataColumns(metadata.statsColumns || metadata.StatsColumns);

      return {
        ...dashboardItem,
        fields: columns,
        listFilter: listFilters,
        chartFilter: chartFilters,
        statsFilter: statsFilters,
        sortFields: sorts,
        data: chartColumns,
        chartGroups,
        stats: statsColumns,
      };
    },

    async loadDashboardOptionsMetadata(key) {
      const dashboardItem = this.widgetList.find((item) => item.key == key);
      if (!dashboardItem?.optionsMode) return null;
      if (this.dashboardOptionsMetadata[key]) return this.dashboardOptionsMetadata[key];

      const cacheKey = `${this.$route.params['user'] || ''}:${key}`;
      if (dashboardOptionsMetadataCache[cacheKey]) {
        this.$set(this.dashboardOptionsMetadata, key, dashboardOptionsMetadataCache[cacheKey]);
        return dashboardOptionsMetadataCache[cacheKey];
      }

      if (!dashboardOptionsRequestCache[cacheKey]) {
        dashboardOptionsRequestCache[cacheKey] = (async () => {
          try {
            const result = await this.$apiCallV2(
              '/Dashboard/GetDashboardData',
              { tablekey: key, mode: 'options' },
              'POST',
              {
                message: `${key} options API failed`,
              },
            );
            if (!result || this.getDashboardApiErrorMessage(result)) return null;
            dashboardOptionsMetadataCache[cacheKey] = result;
            return result;
          } catch (e) {
            return null;
          } finally {
            delete dashboardOptionsRequestCache[cacheKey];
          }
        })();
      }

      const result = await dashboardOptionsRequestCache[cacheKey];
      if (result && !this._isDestroyed) this.$set(this.dashboardOptionsMetadata, key, result);
      return result;
    },

    migrateSavedDashboardWidget(key) {
      if (this.widget.new || this.dashboardOptionsMigratedKey == key) return false;

      const config = this.getWidgetConfig(key);
      if (!config) return false;

      const aliases = key == 'vehicle' ? { vehiclecategorytype: 'vehicletype', vehicletypes: 'vehicletype' } : {};
      const normalizeKey = (value) => aliases[value?.key ?? value] ?? value?.key ?? value;
      const validKeys = (items) => new Set((items || []).map((item) => item.key));
      const filterDefinitions =
        this.widget.widgetType == 'Chart' ? config.chartFilter
        : this.widget.widgetType == 'Stats' ? config.statsFilter
        : this.widget.widgetType == 'DailyReport' ? config.dailyReportFilter
        : config.listFilter;
      const filterKeys = validKeys(filterDefinitions);
      const next = {
        ...this.widget,
        filtersArr: (this.widget.filtersArr || [])
          .map((filter) => ({ ...filter, key: normalizeKey(filter.key) }))
          .filter((filter) => filterKeys.has(filter.key)),
      };

      if (this.widget.widgetType == 'DailyReport' && key == 'freightimport') {
        next.filtersArr = next.filtersArr.map((filter) => {
          if (filter.key != 'reportdate' || !['date', 'date_value'].includes(filter.filterType)) return filter;
          const { value, ...dateRangeFilter } = filter;
          return {
            ...dateRangeFilter,
            filterType: 'date_range',
            from: filter.from || value || null,
            to: filter.to || value || null,
          };
        });
      }

      if (this.widget.widgetType == 'List') {
        const fieldKeys = validKeys(config.fields);
        const primary = config.fields.find((field) => field.primary) || config.fields[0];
        next.detailsFields = (this.widget.detailsFields || []).map(normalizeKey).filter((field) => fieldKeys.has(field));
        next.primaryLeftField = normalizeKey(this.widget.primaryLeftField);
        if (!fieldKeys.has(next.primaryLeftField)) next.primaryLeftField = primary?.key ?? null;
        next.primaryRightField = normalizeKey(this.widget.primaryRightField);
        if (!fieldKeys.has(next.primaryRightField)) next.primaryRightField = null;

        const sortKeys = validKeys(config.sortFields);
        next.sortKey = normalizeKey(this.widget.sortKey);
        if (!sortKeys.has(next.sortKey)) next.sortKey = config.sortFields[0]?.key ?? null;
      } else if (this.widget.widgetType == 'Chart') {
        const chartKeys = validKeys(config.data);
        const chartGroups = config.chartGroups || [];
        const savedGroupKey = normalizeKey(this.widget.chartGroup);
        const savedDataKey = normalizeKey(this.widget.dataToShow);
        const savedChartType = this.normalizeDashboardChartType(normalizeKey(this.widget.chartType));
        next.chartType = savedChartType;
        const legacyMapping = this.getLegacyChartMapping(config, savedDataKey, savedChartType);
        const group = chartGroups.find((item) => item.key == savedGroupKey) ||
          chartGroups.find((item) => item.key == legacyMapping?.group) ||
          chartGroups.find((item) => item.series?.some((series) => series.key == savedDataKey));

        if (group) {
          next.chartGroup = group.key;
          next.dataToShow = legacyMapping?.dataToShow || this.getChartGroupCompatibleDataKey(config, group);
          if (!chartKeys.has(next.dataToShow) && group.series?.[0]?.key) next.dataToShow = group.series[0].key;
          next.chartType = legacyMapping?.targetChartType || this.getChartGroupChartType(group, savedChartType);
          if (!next.chartTime) {
            const legacyDateFilter = (this.widget.filtersArr || []).find((filter) => filter?.key == 'jobdatetime');
            if (legacyDateFilter) {
              const legacyFrom = legacyDateFilter.from || legacyDateFilter.value || '';
              const legacyTo = legacyDateFilter.to || legacyDateFilter.value || legacyFrom;
              next.chartTime = {
                scope: 'date_range',
                valueMode: 'period',
                groupBy: group.defaultGroupBy || 'month',
                from: this.formatDashboardFilterDate(legacyFrom, true),
                to: this.formatDashboardFilterDate(legacyTo, true),
              };
            } else {
              next.chartTime = {
                scope: group.defaultScope || (group.defaultDateScope == 'none' ? 'all' : 'last_days'),
                valueMode: group.defaultValueMode || 'period',
                ...(group.defaultScope == 'all' || group.defaultDateScope == 'none'
                  ? {}
                  : { days: parseInt(group.defaultDays, 10) || 180 }),
                ...(group.defaultGroupBy ? { groupBy: group.defaultGroupBy } : {}),
              };
            }
          }
          const groupFilterKeys = this.getChartGroupFilterKeys(group);
          next.filtersArr = next.filtersArr.filter((filter) => groupFilterKeys.has(filter.key));
        } else {
          delete next.chartGroup;
          next.dataToShow = savedDataKey;
          if (!chartKeys.has(next.dataToShow)) next.dataToShow = config.data[0]?.key ?? null;
          const chart = config.data.find((item) => item.key == next.dataToShow);
          const chartTypeKeys = validKeys(chart?.type);
          if (!chartTypeKeys.has(next.chartType)) next.chartType = chart?.type?.[0]?.key ?? 'Bar';
        }
      } else if (this.widget.widgetType == 'Stats') {
        const statsKeys = validKeys(config.stats);
        next.statsToShow = (this.widget.statsToShow || []).map(normalizeKey).filter((stat) => statsKeys.has(stat));
      }

      this.dashboardOptionsMigratedKey = key;
      if (JSON.stringify(next) == JSON.stringify(this.widget)) return false;
      this.$emit('save', next);
      return true;
    },

    contextMenuBtnClick() {
      this.contextMenuIsOpen = !this.contextMenuIsOpen;
    },
    handleOutsideClick(event) {
      if (!this.contextMenuIsOpen) return;
      if (this.$refs.contextMenu && !this.$refs.contextMenu.contains(event.target)) {
        this.contextMenuIsOpen = false;
      }
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    getFilterDisplayValue(filter, definition) {
      if (filter.filterType == 'dropdown_list') {
        const option = definition?.options?.find((item) => item.key == filter.value);
        return option?.label || filter.value || '';
      }
      if (filter.filterType == 'boolean') {
        if (filter.value === true) return 'Yes';
        if (filter.value === false) return 'No';
        return '';
      }
      if (filter.filterType == 'date_range') {
        return `${filter.from || 'Start'} to ${filter.to || 'Today'}`;
      }
      if (filter.filterType == 'date') return this.formatDashboardFilterDate(filter.value, true) || '';
      if (filter.filterType == 'date_value') return filter.value || '';
      if (filter.filterType == 'multiselect') {
        const saveKey = filter.saveKey || 'key';
        return (filter.selected || [])
          .map((item) => (item && typeof item == 'object' ? item.label || item[saveKey] : item))
          .join(', ');
      }
      if (filter.filterType == 'string_range') return `${filter.fromStr || ''} to ${filter.toStr || ''}`;
      return filter.value || '';
    },

    widgetEditClearFields() {
      this.widgetEditValue = null;
      this.widgetTypeEditValue = null;
      this.widgetHeightValue = this.widgetSizeOptions[0];
      this.widgetWidthValue = this.widgetSizeOptions[0];
      this.widgetTemplateValue = null;
      this.primaryLeftFieldValue = null;
      this.primaryRightFieldValue = null;
      this.detailFieldsEdit = [];
      this.sortByValue = null;
      this.sortByDescValue = true;
      this.maxRowsValue = '10';
      this.filterValue = [];
      this.editNewFieldInputValue = '';
      this.customTitleValue = '';

      this.dataToShowValue = null;
      this.chartGroupValue = null;
      this.chartTypeValue = null;
      this.chartDateScopeValue = null;
      this.chartValueModeValue = null;
      this.chartGroupByValue = null;
      this.chartTimeDaysValue = '7';
      this.chartTimeFromValue = '';
      this.chartTimeToValue = '';

      this.buttonFieldsEdit = [];
      this.editNewButtonValue = null;
      this.editNewButtonSearch = '';

      this.widgetEditValueError = '';
      this.widgetTypeEditValueError = '';

      this.primaryLeftFieldValueError = '';
      this.sortByValueError = '';

      this.chartGroupValueError = '';
      this.chartTypeValueError = '';

      this.statsFieldsEdit = [];
    },

    async widgetEditClicked() {
      this.editMode = true;
      this.contextMenuIsOpen = false;
      this.widgetEditClearFields();
      const { key, widgetType, filtersArr } = this.widgetObject;
      this.customTitleValue = this.widgetObject.customTitle || '';
      await this.loadDashboardOptionsMetadata(key);
      this.widgetEditValue = this.getWidgetConfig(key);
      this.widgetTypeEditValue = this.widgetTypeListFiltered.find((item) => item.value == widgetType);
      this.filterValue = filtersArr ? [...filtersArr] : [];
      this.widgetHeightValue =
        this.widgetSizeOptions.find((item) => item.value == this.normalizeWidgetSpan(this.widgetObject.rowSpan)) ||
        this.widgetSizeOptions[0];
      this.widgetWidthValue =
        this.widgetSizeOptions.find((item) => item.value == this.normalizeWidgetSpan(this.widgetObject.colSpan)) ||
        this.widgetSizeOptions[0];
      const savedTemplateKey = this.widgetObject.templateKey;
      this.widgetTemplateValue = null;

      if (widgetType == 'List') {
        const { primaryLeftField, primaryRightField, detailsFields, sortKey, sortDesc, maxRows } = this.widgetObject;
        this.primaryLeftFieldValue = primaryLeftField;
        this.primaryRightFieldValue = primaryRightField;
        this.detailFieldsEdit = [...(detailsFields ?? [])];
        this.sortByValue = this.editFieldsListSort.find((item) => item.key == sortKey);
        this.sortByDescValue = sortDesc;
        this.maxRowsValue = String(maxRows ?? 10);
        this.detailsFieldsOpenItems = [];
      } else if (widgetType == 'Chart') {
        const { chartGroup, dataToShow, chartType } = this.widgetObject;
        this.chartGroupValue = this.chartGroupList.find((item) => item.key == chartGroup) || null;
        if (this.chartGroupValue) {
          const chartFilterKeys = this.getChartGroupFilterKeys(this.chartGroupValue);
          this.filterValue = this.filterValue.filter((filter) => chartFilterKeys.has(filter.key));
          this.dataToShowValue = null;
          const selectedChartType = this.getChartGroupChartType(this.chartGroupValue, chartType);
          this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == selectedChartType) || null;
        } else {
          this.dataToShowValue = dataToShow;
          this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == chartType);
        }
        this.setChartTimeState(this.widgetObject.chartTime, this.chartGroupValue);
      } else if (widgetType == 'Buttons') {
        const { buttonFields } = this.widgetObject;
        this.buttonFieldsEdit = [...(buttonFields ?? [])];
      } else if (widgetType == 'Stats') {
        const { statsToShow } = this.widgetObject;
        this.statsFieldsEdit = [...(statsToShow ?? [])];
      }

      const customTemplate = this.widgetTemplateList.find((item) => item.key == 'custom') || null;
      const savedTemplate = this.widgetTemplateList.find((item) => item.key == savedTemplateKey) || null;
      this.widgetTemplateValue =
        savedTemplateKey == 'custom' ? customTemplate
        : savedTemplateKey && !savedTemplate ? customTemplate
        : savedTemplate || customTemplate || this.widgetTemplateList[0] || null;

      this.$nextTick(() => {
        if (this.$refs.editContainer) this.$refs.editContainer.scrollTop = 0;
      });
    },

    widgetDuplicateClicked() {
      this.$emit('duplicate', this.widget);
      this.contextMenuIsOpen = false;
    },

    cancelEdit() {
      if (this.widgetObjectEmpty) this.$emit('delete', this.widgetObject.id, this.widgetObject.new);
      this.editMode = false;
      if (this.widgetObject.widgetType == 'List')
        this.$nextTick(() => {
          if (this.$refs.detailsList) this.$refs.detailsList.scrollTop = 0;
        });
    },

    saveEdit() {
      let error = false;
      if (!this.widgetEditValue) {
        this.widgetEditValueError = 'Widget is Required!';
        error = true;
      }
      if (!this.widgetTypeEditValue) {
        this.widgetTypeEditValueError = 'Widget Type is Required!';
        error = true;
      }

      if (error) return;

      const { key } = this.widgetEditValue || {};
      const widgetType = this.widgetTypeEditValue?.value ?? '';

      const save = {
        ...this.widget,
        key: key,
        widgetType: widgetType,
        customTitle: this.customTitleValue.trim(),
        rowSpan: this.normalizeWidgetSpan(this.widgetHeightValue?.value),
        colSpan: this.normalizeWidgetSpan(this.widgetWidthValue?.value),
        templateKey: this.widgetTemplateValue?.key || null,
      };

      if (widgetType == 'List') {
        let error = false;

        if (!this.primaryLeftFieldValue) {
          this.primaryLeftFieldValueError = 'Primary Left Field is Required!';
          error = true;
        }
        if (!this.sortByValue) {
          this.sortByValueError = 'Sort By Field is Required!';
          error = true;
        }

        if (error) return;

        save.primaryLeftField = this.primaryLeftFieldValue.key;
        save.primaryRightField = this.primaryRightFieldValue?.key ?? null;
        save.detailsFields = this.detailFieldsEdit.map((item) => item.key);
        save.sortKey = this.sortByValue?.key;
        save.sortDesc = this.sortByDescValue;
        save.maxRows = Math.min(parseInt(this.maxRowsValue), 100);
      } else if (widgetType == 'Chart') {
        let error = false;
        if (this.chartGroupValue) {
          save.chartGroup = this.chartGroupValue.key;
          save.dataToShow = this.getChartGroupCompatibleDataKey(this.widgetEditValue, this.chartGroupValue);
          save.chartType = this.getChartGroupChartType(this.chartGroupValue, this.chartTypeValue?.key);
        } else {
          const keepLegacyChart = !this.widgetObjectEmpty && this.widgetObject.key == key && this.widgetObject.dataToShow;
          if (keepLegacyChart) {
            delete save.chartGroup;
            save.dataToShow = this.widgetObject.dataToShow;
            save.chartType = this.widgetObject.chartType;
          } else {
            this.chartGroupValueError = 'Chart Group is Required!';
            error = true;
          }
        }
        if (this.chartGroupValue) {
          const chartTime = this.getChartTimeState();
          if (
            chartTime.scope == 'last_days' &&
            (!Number.isInteger(Number(chartTime.days)) || Number(chartTime.days) < 1 || Number(chartTime.days) > 3650)
          ) {
            this.chartGroupValueError = 'Enter a valid number of days!';
            error = true;
          } else if (chartTime.scope == 'date_range' && (!chartTime.from || !chartTime.to)) {
            this.chartGroupValueError = 'Date range requires a start and end date!';
            error = true;
          } else if (chartTime.from && chartTime.to && chartTime.from > chartTime.to) {
            this.chartGroupValueError = 'Chart start date must be before end date!';
            error = true;
          }
          save.chartTime = chartTime;
        }
        if (error) return;
      } else if (widgetType == 'Buttons') {
        save.buttonFields = this.buttonFieldsEdit.map((item) => item.key);
      } else if (widgetType == 'Stats') {
        save.statsToShow = this.statsFieldsEdit.map((item) => item.key);
      }

      if (this.filterValue) {
        const filtersToSave = widgetType == 'Chart' && this.chartGroupValue
          ? this.filterValue.filter((item) => item.key != 'jobdatetime')
          : this.filterValue;
        save.filtersArr = filtersToSave.map((item) => {
          const { key, joinType, value, selected, to, from, toStr, fromStr, filterType } = item;

          switch (filterType) {
            case 'string_value':
              return { key, filterType, joinType, value };
            case 'string_range':
              return { key, filterType, joinType, fromStr, toStr };
            case 'date':
              return {
                key,
                filterType,
                joinType,
                value: this.formatDashboardFilterDate(value, true),
              };
            case 'date_value':
              return {
                key,
                filterType,
                joinType,
                value: this.formatDashboardFilterDate(value),
              };
            case 'date_range': {
              const dateOnly = this.isDashboardDateOnlyFilter(item);
              return {
                key,
                filterType,
                joinType,
                from: this.formatDashboardFilterDate(from, dateOnly),
                to: this.formatDashboardFilterDate(to, dateOnly),
              };
            }
            case 'multiselect':
              return { key, filterType, joinType, selected };
            default:
              return { key, filterType, joinType, value };
          }
        });
      }

      this.$emit('save', save);

      this.detailsFieldsOpenItems = [];

      this.editMode = false;

      if (widgetType == 'List')
        this.$nextTick(() => {
          if (this.$refs.detailsList) this.$refs.detailsList.scrollTop = 0;
        });
    },

    removeClicked() {
      this.$emit('delete', this.widget.id, this.widget.new);
    },

    async widgetEditSelected() {
      const selectedKey = this.widgetEditValue?.key;
      await this.loadDashboardOptionsMetadata(selectedKey);
      if (selectedKey) this.widgetEditValue = this.getWidgetConfig(selectedKey) || this.widgetEditValue;
      this.widgetTypeEditValue = null;
      this.widgetHeightValue = this.widgetSizeOptions[0];
      this.widgetWidthValue = this.widgetSizeOptions[0];
      this.widgetTemplateValue = null;
      if (this.widgetTypeListFiltered.length <= 1) {
        this.widgetTypeEditValue = this.widgetTypeListFiltered[0];
        this.widgetTypeSelected();
      }
    },

    normalizeWidgetSpan(value) {
      return Number(value) == 2 ? 2 : 1;
    },

    getLegacyChartDateScopes(group) {
      const chartTypes = (group?.chartTypes?.length ? group.chartTypes : [group?.chartType]).map((type) =>
        typeof type == 'string' ? type : type?.key,
      );
      return chartTypes.includes('Doughnut')
        ? ['today', 'last_days', 'this_month', 'this_year', 'date_range', 'all']
        : ['today', 'last_days', 'this_month', 'this_year', 'date_range'];
    },

    getDateOnlyValue(date) {
      return date && typeof date.formatString == 'function'
        ? date.formatString('yyyy-MM-dd')
        : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },

    parseDateOnlyValue(value) {
      if (!value) return null;
      const [year, month, day] = String(value).split('-').map((item) => Number(item));
      if (!year || !month || !day) return null;
      const date = new Date(year, month - 1, day);
      return date.getFullYear() == year && date.getMonth() == month - 1 && date.getDate() == day ? date : null;
    },

    normalizeLegacyChartTime(chartTime = {}, group = this.chartGroupValue) {
      const legacyWindow = String(chartTime.window || '').toLowerCase();
      const today = new Date();
      const todayValue = this.getDateOnlyValue(today);
      const legacyState = {
        scope: 'last_days',
        days: 180,
        valueMode: chartTime.mode == 'cumulative_since' ? 'cumulative' : 'period',
        from: '',
        to: '',
      };

      if (legacyWindow == 'all') {
        legacyState.scope = 'all';
      } else if (legacyWindow == 'today') {
        legacyState.scope = 'today';
      } else if (legacyWindow == 'last_7_days' || legacyWindow == 'last_30_days') {
        legacyState.scope = 'last_days';
        legacyState.days = legacyWindow == 'last_7_days' ? 7 : 30;
      } else if (legacyWindow == 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        legacyState.scope = 'date_range';
        legacyState.from = this.getDateOnlyValue(yesterday);
        legacyState.to = legacyState.from;
      } else if (legacyWindow == 'this_month') {
        legacyState.scope = 'this_month';
      } else if (legacyWindow == 'ytd') {
        legacyState.scope = 'this_year';
      } else if (legacyWindow == 'custom' || chartTime.from || chartTime.to) {
        legacyState.scope = 'date_range';
        legacyState.from = chartTime.from || '';
        legacyState.to = chartTime.to || todayValue;
      }

      if (group?.defaultDateScope == 'none' && !chartTime.window) legacyState.scope = 'all';
      return legacyState;
    },

    setChartTimeState(chartTime, group = this.chartGroupValue) {
      const saved = chartTime || {};
      const legacyState = saved.scope ? saved : this.normalizeLegacyChartTime(saved, group);
      const defaultScope = group?.defaultScope || (group?.defaultDateScope == 'none' ? 'all' : 'last_days');
      const defaultDays = parseInt(group?.defaultDays, 10) || 180;
      const defaultValueMode = group?.defaultValueMode || 'period';
      const defaultGroupBy = group?.defaultGroupBy || 'month';
      this.chartDateScopeValue =
        this.chartDateScopeList.find((item) => item.key == (legacyState.scope || defaultScope)) ||
        this.chartDateScopeList[0] ||
        null;
      this.chartValueModeValue =
        this.chartValueModeList.find((item) => item.key == (legacyState.valueMode || defaultValueMode)) ||
        this.chartValueModeList[0] ||
        null;
      this.chartGroupByValue =
        this.chartGroupByList.find((item) => item.key == (saved.groupBy || defaultGroupBy)) || this.chartGroupByList[0] || null;
      this.chartTimeDaysValue = String(legacyState.days || defaultDays);
      this.chartTimeFromValue = legacyState.from || '';
      this.chartTimeToValue = legacyState.to || '';
    },

    getTemplateListFields(defaults = {}) {
      const fields = this.editFieldsList || [];
      const findField = (value) => {
        if (!value) return null;
        if (value == 'primary') return this.getDefaultPrimaryLeftField();
        return fields.find((item) => item.key == value) || null;
      };

      const primaryLeft = findField(defaults.primaryLeft) || this.getDefaultPrimaryLeftField();
      const usedKeys = new Set([primaryLeft?.key].filter((item) => item));
      const availableFields = fields.filter((item) => !usedKeys.has(item.key));
      let primaryRight = defaults.primaryRight == 'next' ? availableFields[0] || null : findField(defaults.primaryRight);
      if (primaryRight?.key == primaryLeft?.key) primaryRight = availableFields[0] || null;
      if (primaryRight) usedKeys.add(primaryRight.key);

      const detailCount = Math.max(parseInt(defaults.detailsCount) || 0, 0);
      const detailsFields = fields.filter((item) => !usedKeys.has(item.key)).slice(0, detailCount);
      const sortBy = defaults.sort == 'first' ? this.editFieldsListSort[0] || null :
        this.editFieldsListSort.find((item) => item.key == defaults.sort) || null;

      return { primaryLeft, primaryRight, detailsFields, sortBy };
    },

    getTemplateChartGroup(kind) {
      const groups = this.chartGroupList || [];
      if (groups.length <= 0) return null;

      const isDoughnutGroup = (group) => {
        const chartTypes = (group.chartTypes?.length ? group.chartTypes : [group.chartType]).map((type) =>
          typeof type == 'string' ? type : type?.key,
        );
        return chartTypes.includes('Doughnut');
      };

      if (kind == 'distribution') return groups.find((group) => isDoughnutGroup(group)) || groups[0];
      return groups.find((group) => !isDoughnutGroup(group)) || groups[0];
    },

    getChartTimeState() {
      const state = {
        scope: this.chartDateScopeValue?.key || 'last_days',
        valueMode: this.chartValueModeValue?.key || 'period',
      };
      if (state.scope == 'last_days') {
        const days = Number(this.chartTimeDaysValue);
        state.days = Number.isFinite(days) ? days : 0;
      }
      if (state.scope == 'date_range') {
        if (this.chartTimeFromValue) state.from = this.chartTimeFromValue;
        if (this.chartTimeToValue) state.to = this.chartTimeToValue;
      }
      if (this.chartGroupByValue && state.scope != 'all') state.groupBy = this.chartGroupByValue.key;
      return state;
    },

    applyWidgetTemplate(template) {
      if (!template || template.key == 'custom') return;
      const defaults = template?.defaults || {};
      const widgetType = this.widgetTypeEditValue?.value;

      if (widgetType == 'List' && defaults.listFields) {
        const listFields = this.getTemplateListFields(defaults.listFields);
        this.primaryLeftFieldValue = listFields.primaryLeft;
        this.primaryRightFieldValue = listFields.primaryRight;
        this.detailFieldsEdit = listFields.detailsFields.map((item) => ({ ...item, id: item.id || item.key }));
        this.sortByValue = listFields.sortBy;
        this.sortByDescValue =
          listFields.sortBy && Object.prototype.hasOwnProperty.call(listFields.sortBy, 'defaultDesc') ?
            listFields.sortBy.defaultDesc
          : true;
        if (defaults.maxRows !== undefined) this.maxRowsValue = String(defaults.maxRows);
      }

      if (widgetType == 'Chart' && (defaults.chartGroup || defaults.chartType || defaults.chartTime)) {
        const chartGroup = defaults.chartGroup ? this.getTemplateChartGroup(defaults.chartGroup) : this.chartGroupValue;
        if (chartGroup) {
          this.chartGroupValue = chartGroup;
          this.dataToShowValue = null;
          const chartType = this.getChartGroupChartType(chartGroup, defaults.chartType);
          this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == chartType) || this.chartTypeValue;
          const groupFilterKeys = this.getChartGroupFilterKeys(chartGroup);
          this.filterValue = this.filterValue.filter((filter) => groupFilterKeys.has(filter.key));
        } else if (defaults.chartType) {
          this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == defaults.chartType) || this.chartTypeValue;
        }

        if (defaults.chartTime) {
          this.setChartTimeState(defaults.chartTime, this.chartGroupValue);
        }
      }

      if (widgetType == 'Stats' && defaults.statsCount !== undefined) {
        this.statsFieldsEdit = (this.widgetEditValue?.stats || [])
          .slice(0, Math.max(parseInt(defaults.statsCount) || 0, 0))
          .map((item) => ({ ...item, id: item.id || item.key }));
      }

      if (widgetType == 'Buttons' && defaults.buttonCount !== undefined) {
        this.buttonFieldsEdit = this.editButtonList.slice(0, Math.max(parseInt(defaults.buttonCount) || 0, 0));
      }

    },

    widgetTemplateSelected(template) {
      const selectedTemplate =
        this.widgetTemplateList.find((item) => item.key == template?.key) ||
        this.widgetTemplateList.find((item) => item.key == 'custom') ||
        this.widgetTemplateList[0] ||
        null;
      this.widgetTemplateValue = selectedTemplate;
      if (selectedTemplate?.key != 'custom') this.applyWidgetTemplate(selectedTemplate);
    },

    markTemplateCustom() {
      if (!this.editMode || !this.widgetTypeEditValue) return;
      const customTemplate = this.widgetTemplateList.find((item) => item.key == 'custom') || null;
      if (customTemplate) this.widgetTemplateValue = customTemplate;
    },

    chartDateScopeSelected(scope) {
      if (scope?.key != 'date_range') {
        this.chartTimeFromValue = '';
        this.chartTimeToValue = '';
      }
      if (scope?.key != 'last_days') this.chartTimeDaysValue = '7';
      if (scope?.key == 'all') this.chartGroupByValue = null;
      if (scope?.key != 'all' && !this.chartGroupByValue) this.chartGroupByValue = this.chartGroupByList[0] || null;
      this.markTemplateCustom();
    },

    widgetTypeSelected() {
      if (this.widgetTypeEditValue?.value == 'List') {
        this.primaryLeftFieldValue = this.getDefaultPrimaryLeftField();
        this.setDefaultListSort();
      } else if (this.widgetTypeEditValue?.value == 'Chart') {
        this.chartGroupValue = this.chartGroupList[0] || null;
        const chartType = this.chartGroupValue ? this.getChartGroupChartType(this.chartGroupValue) : null;
        this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == chartType) || null;
        this.setChartTimeState(null, this.chartGroupValue);
      }
      this.widgetTemplateValue = this.widgetTemplateList.find((item) => item.key != 'custom') ||
        this.widgetTemplateList.find((item) => item.key == 'custom') ||
        this.widgetTemplateList[0] ||
        null;
      this.applyWidgetTemplate(this.widgetTemplateValue);
      this.filterValue = [];
    },

    chartGroupSelected(group) {
      this.chartGroupValue = group || this.chartGroupValue;
      this.dataToShowValue = null;
      const chartType = this.getChartGroupChartType(this.chartGroupValue);
      this.chartTypeValue = this.chartEditTypeList.find((item) => item.key == chartType) || null;
      this.setChartTimeState(null, this.chartGroupValue);
      const groupFilterKeys = this.getChartGroupFilterKeys(this.chartGroupValue);
      this.filterValue = this.filterValue.filter((filter) => groupFilterKeys.has(filter.key));
      this.markTemplateCustom();
    },

    getDefaultPrimaryLeftField() {
      const primaryField = this.editFieldsPrimaryListFiltered[0];
      if (primaryField) return primaryField;

      const configuredPrimaryKey = this.widgetList
        .find((item) => item.key == this.widgetEditValue?.key)
        ?.fields?.find((item) => item.primary)?.key;
      return this.editFieldsList.find((item) => item.key == configuredPrimaryKey) || null;
    },

    setDefaultListSort() {
      const defaultSort = this.editFieldsListSort[0];
      this.sortByValue = defaultSort ?? null;
      this.sortByDescValue =
        defaultSort && Object.prototype.hasOwnProperty.call(defaultSort, 'defaultDesc') ?
          defaultSort.defaultDesc
        : true;
    },

    getListActionItem(item) {
      const primaryField = this.widgetObject?.fields?.find((field) => field.primary);
      if (!primaryField) return item;

      return {
        ...item,
        primaryKey: primaryField.key,
        primaryValue: item[primaryField.key],
      };
    },

    toggleDetailsMenuOpen(i) {
      const idx = this.detailsMenuOpenItems.indexOf(i);
      if (idx == -1) {
        this.detailsMenuOpenItems = [i];
      } else {
        this.detailsMenuOpenItems = [];
      }
    },
    isDetailsMenuOpen(i) {
      return this.detailsMenuOpenItems.includes(i);
    },

    handleOutsideDetailsMenuClick(e) {
      const clickedInsideAnyMenu = this.detailsMenuOpenItems.some((i) => {
        const menuEl = this.$refs['contextMenu-' + i];
        const el = Array.isArray(menuEl) ? menuEl[0] : menuEl;
        return el && el.contains(e.target);
      });
      if (!clickedInsideAnyMenu) {
        this.detailsMenuOpenItems = [];
      }
    },

    toggleDetailsFieldsOpen(i) {
      const idx = this.detailsFieldsOpenItems.indexOf(i);
      if (idx == -1) {
        this.detailsFieldsOpenItems.push(i);
      } else {
        this.detailsFieldsOpenItems.splice(idx, 1);
      }
    },
    isDetailsFieldsOpen(i) {
      return this.detailsFieldsOpenItems.includes(i);
    },

    //dragable field edits

    getVisualDetailFieldKeys() {
      const container = this.$refs.listFieldConfig;
      if (!container) return [];
      return Array.from(container.querySelectorAll('.field-config-item'))
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return {
            key: el.dataset.fieldKey,
            top: rect.top,
            left: rect.left,
          };
        })
        .sort((a, b) => a.top - b.top || a.left - b.left)
        .map((item) => item.key)
        .filter((key) => key);
    },
    normalizeDetailFieldsToVisualOrder() {
      const visualKeys = this.getVisualDetailFieldKeys();
      if (visualKeys.length != this.detailFieldsEdit.length) return;

      const fieldMap = this.detailFieldsEdit.reduce((acc, field) => {
        acc[field.key] = field;
        return acc;
      }, {});
      const visualFields = visualKeys.map((key) => fieldMap[key]).filter((field) => field);
      if (visualFields.length == this.detailFieldsEdit.length) this.detailFieldsEdit = visualFields;
    },
    onFieldConfigItemDragStart(index, col) {
      this.normalizeDetailFieldsToVisualOrder();
      this.dragIndex = this.detailFieldsEdit.findIndex((field) => field.key == col.key);
      if (this.dragIndex == -1) this.dragIndex = index;
    },
    onFieldConfigItemDragOver(index, e) {
      if (this.dragIndex === null || this.dragIndex === index) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const hoverMiddleX = (rect.right - rect.left) / 2;
      const hoverClientX = e.clientX - rect.left;
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const hoverClientY = e.clientY - rect.top;
      const useHorizontal = rect.width > rect.height * 2;

      if (this.dragIndex < index && (useHorizontal ? hoverClientX < hoverMiddleX : hoverClientY < hoverMiddleY)) return;
      if (this.dragIndex > index && (useHorizontal ? hoverClientX > hoverMiddleX : hoverClientY > hoverMiddleY)) return;

      const cols = [...this.detailFieldsEdit];
      const dragged = cols.splice(this.dragIndex, 1)[0];
      cols.splice(index, 0, dragged);

      this.detailFieldsEdit = cols;
      this.dragIndex = index;
      this.markTemplateCustom();
    },
    onFieldConfigItemDragEnd() {
      this.dragIndex = null;
    },

    getTouchPoint(event) {
      return event.touches?.[0] || event.changedTouches?.[0] || null;
    },

    onFieldConfigItemTouchStart(index, col, event) {
      if (event.touches?.length > 1) return;
      this.onFieldConfigItemDragStart(index, col);
    },

    onFieldConfigItemTouchMove(event) {
      if (this.dragIndex === null) return;
      const touch = this.getTouchPoint(event);
      if (!touch) return;

      const el = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('.field-config-item[data-field-key]');
      const key = el?.dataset?.fieldKey;
      const index = this.detailFieldsEdit.findIndex((field) => field.key == key);
      if (index == -1 || index == this.dragIndex) return;

      this.onFieldConfigItemDragOver(index, {
        currentTarget: el,
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
    },

    onFieldConfigItemTouchEnd() {
      this.onFieldConfigItemDragEnd();
    },

    // end drag

    detailsFieldDelete(i) {
      this.detailFieldsEdit.splice(i, 1);
      this.markTemplateCustom();
    },

    editNewFieldSelected(e) {
      if (e) {
        this.detailFieldsEdit.push({ ...e, id: crypto.randomUUID() });
        this.markTemplateCustom();
      }
      this.$nextTick(() => {
        this.editNewFieldInputValue = '';
      });
    },

    maxRowsFocusLost() {
      if (!this.maxRowsValue) this.maxRowsValue = 10;
      this.maxRowsValue = Math.min(Math.max(parseInt(this.maxRowsValue), 1), 100).toString();
      this.markTemplateCustom();
    },

    formatDashboardFilterDate(value, dateOnly = false) {
      if (!value) return null;
      const date = value instanceof Date ? value : new Date(value);
      if (date == 'Invalid Date' || Number.isNaN(date.getTime())) return value;
      return date.formatString(dateOnly ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm');
    },

    isDashboardDateOnlyFilter(filter = {}) {
      if (filter.showTime === false) return true;
      return ['jobdatetime', 'eta', 'closingtime', 'reportdate'].includes(String(filter.key || '').toLowerCase());
    },

    normalizeDashboardFiltersForApi(filtersArr = []) {
      return filtersArr.map((filter) => {
        if (filter.filterType == 'date') {
          return {
            ...filter,
            filterType: 'date_value',
            value: this.formatDashboardFilterDate(filter.value, true),
          };
        }

        if (filter.filterType == 'date_value') {
          return {
            ...filter,
            value: this.formatDashboardFilterDate(filter.value),
          };
        }

        if (filter.filterType == 'date_range') {
          const dateOnly = this.isDashboardDateOnlyFilter(filter);
          return {
            ...filter,
            from: this.formatDashboardFilterDate(filter.from, dateOnly),
            to: this.formatDashboardFilterDate(filter.to, dateOnly),
          };
        }

        if (filter.filterType == 'multiselect') {
          const saveKey = filter.saveKey || 'key';
          return {
            ...filter,
            selected: (filter.selected || []).map((item) => (item && typeof item == 'object' ? item[saveKey] : item)),
          };
        }

        return filter;
      });
    },

    getDailyReportPayload(filtersArr = []) {
      const dateFilter = filtersArr.find((filter) => filter.key == 'reportdate') || {};
      const today = new Date().formatString('yyyy-MM-dd');
      const dateValue = dateFilter.to || dateFilter.value;
      const dateFrom = dateFilter.from ? this.formatDashboardFilterDate(dateFilter.from, true) : '';
      const payload = {
        reporttype: 'DailyReport',
        jobtype: 'Import',
        dateto: this.formatDashboardFilterDate(dateValue, true) || today,
      };

      if (dateFrom) payload.datefrom = dateFrom;

      ['has1253a', 'includereleasedbeforecutoff'].forEach((key) => {
        const booleanFilter = filtersArr.find((filter) => filter.key == key);
        if (booleanFilter && booleanFilter.value !== null && booleanFilter.value !== undefined) {
          payload[key] =
            booleanFilter.value === true || String(booleanFilter.value).toLowerCase() == 'true';
        }
      });

      return payload;
    },

    normalizeDailyReportItems(rows = []) {
      return rows.map((row) => ({
        title: row.title ?? row.Title ?? row.analytic ?? row.Analytic ?? '',
        value: row.value ?? row.Value ?? row.jobCount ?? row.JobCount ?? row['No. of jobs'] ?? row['No. Of Jobs'] ?? 0,
        percentage: row.percentage ?? row.Percentage ?? row['Percentage (%)'] ?? null,
        category: row.category ?? row.Category ?? row.type ?? row.Type ?? '',
      }));
    },

    getDashboardApiErrorMessage(result) {
      let response = result;
      if (typeof response == 'string') {
        if (response == '1') return 'Unable to load widget data.';
        try {
          response = JSON.parse(response);
        } catch (parseError) {
          return '';
        }
      }

      const responseItem = Array.isArray(response) ? response[0] : response;
      const resultFlag = responseItem?.Results ?? responseItem?.result;
      if (resultFlag != '1' && resultFlag != 1) return '';

      return responseItem?.ErrMsg || 'Unable to load widget data.';
    },

    clearDashboardData() {
      this.detailsItems = [];
      this.dataToChart = { labels: [], data: [], datasets: [], dataLabel: '' };
      this.statsItems = [];
      this.dailyReportItems = [];
      this.notificationsList = [];
    },

    async getDashboardApiData(url, payload, message) {
      const result = await this.$apiCallV2(url, payload, 'POST', {
        message,
      });
      if (result == null) {
        this.clearDashboardData();
        this.apiErrorMessage = 'Unable to load widget data.';
        return null;
      }
      const errorMessage = this.getDashboardApiErrorMessage(result);
      if (errorMessage) {
        this.clearDashboardData();
        this.apiErrorMessage = errorMessage;
        return null;
      }
      return result;
    },

    openDailyReportJobs(item) {
      this.selectedDailyReportItem = item;
      this.showDailyReportPopup = true;
    },

    async getData() {
      const { key: tableKey, widgetType } = this.widgetObject || {};
      if (!tableKey) return;
      this.apiErrorMessage = '';
      this.clearDashboardData();
      this.isWidgetLoading = true;

      if (tableKey == 'noficiations') await this.getNofitications();

      if (widgetType == 'List') {
        const { detailsFields, sortKey, sortDesc, primaryLeftField, primaryRightField, maxRows, filtersArr } =
          this.widgetObject || {};
        const primaryKeyFields = this.widgetObject?.fields?.filter((item) => item.primary) || [];
        const { key: sortByKey } = this.sortByValue || {};
        const { key: leftKey } = this.primaryLeftFieldValue || {};
        const { key: rightKey } = this.primaryRightFieldValue || {};
        const columnsKeyToGet = [
          ...new Set([
            ...detailsFields,
            ...primaryKeyFields,
            primaryLeftField ?? leftKey,
            primaryRightField ?? rightKey,
          ]),
        ]
          .filter((k) => k)
          .map((item) => item.key);
        const payload = {
          tablekey: tableKey,
          mode: 'list',
          columnkeys: columnsKeyToGet.filter((i) => i),
          sortcolumnkey: sortKey ?? sortByKey,
          sortdesc: sortDesc ?? this.sortByDescValue,
          maxrows: maxRows ?? this.maxRowsValue,
          filtersarr: this.normalizeDashboardFiltersForApi(filtersArr),
        };
        if (columnsKeyToGet.length > 0) {
          const result = await this.getDashboardApiData(
            '/Dashboard/GetDashboardData',
            payload,
            `${tableKey} ${widgetType} API failed`,
          );
          if (!this.apiErrorMessage) this.detailsItems = result || [];
        }
      } else if (widgetType == 'Chart') {
        const { dataToShow, chartGroup, filtersArr } = this.widgetObject || {};
        const { key: dataToShowKey } = this.dataToShowValue || {};
        const payload = chartGroup
          ? {
              tablekey: tableKey,
              mode: 'chart',
              chartgroupkey: chartGroup,
              datatable: true,
              filtersarr: this.normalizeDashboardFiltersForApi(filtersArr),
              ...(this.widgetObject.chartTime ? { charttime: this.widgetObject.chartTime } : {}),
            }
          : {
              tablekey: tableKey,
              mode: 'chart',
              datatable: true,
              columnkeys: [dataToShow?.key || dataToShowKey],
              filtersarr: this.normalizeDashboardFiltersForApi(filtersArr),
            };
        const result = await this.getDashboardApiData(
          '/Dashboard/GetDashboardData',
          payload,
          `${tableKey} ${widgetType} API failed`,
        );

        if (!this.apiErrorMessage) {
          const chartData = chartGroup ? this.convertToGroupedChartData(result) : this.convertToChartData(result);
          this.dataToChart = {
            labels: chartData.labels,
            data: chartData.data,
            datasets: chartData.datasets || [],
            dataLabel: dataToShow?.label ?? '',
          };
          this.chartDataVersion++;
        }
      } else if (widgetType == 'Stats') {
        const { statsToShow, filtersArr } = this.widgetObject || {};
        const columnkeys = statsToShow.map((item) => item?.key).filter((item) => item);
        if (columnkeys.length <= 0) {
          this.statsItems = [];
          this.isWidgetLoading = false;
          return;
        }
        const payload = {
          tablekey: tableKey,
          mode: 'stats',
          columnkeys,
          filtersarr: this.normalizeDashboardFiltersForApi(filtersArr),
        };
        const result = await this.getDashboardApiData(
          '/Dashboard/GetDashboardData',
          payload,
          `${tableKey} ${widgetType} API failed`,
        );

        if (!this.apiErrorMessage) this.statsItems = result?.[0] || {};
      } else if (widgetType == 'DailyReport') {
        const { filtersArr } = this.widgetObject || {};
        const payload = this.getDailyReportPayload(this.normalizeDashboardFiltersForApi(filtersArr));
        const result = await this.getDashboardApiData(
          '/Dashboard/ImportDailyReport',
          payload,
          `${tableKey} ${widgetType} API failed`,
        );

        if (!this.apiErrorMessage) {
          const dailyReportRows = result?.items ||
            result?.Items || [
              ...(result?.snapshot || result?.Snapshot || []),
              ...(result?.exceptions || result?.Exceptions || []),
            ];
          this.dailyReportItems = this.normalizeDailyReportItems(dailyReportRows);
        }
      }

      if (this._isDestroyed) return;

      this.detailsFieldsOpenItems = [];

      this.isWidgetLoading = false;
    },

    convertToChartData(result) {
      if (!result?.length) {
        return { labels: [], data: [] };
      }

      return {
        labels: result.map((x) => x.Label),
        data: result.map((x) => Number(x.Value)),
      };
    },

    convertToGroupedChartData(result) {
      const series = result?.series || result?.Series || [];
      if (!Array.isArray(series)) return { labels: [], data: [], datasets: [] };

      const labels = [];
      series.forEach((item) => {
        const rows = item?.items || item?.Items || [];
        rows.forEach((row) => {
          const label = row?.Label ?? row?.label;
          if (label !== undefined && !labels.includes(label)) labels.push(label);
        });
      });

      const datasets = series.map((item) => {
        const rows = item?.items || item?.Items || [];
        return {
          label: item?.label || item?.Label || item?.key || item?.Key || '',
          unit: item?.unit || item?.Unit || 'jobs',
          precision: (() => {
            const value = parseInt(item?.precision ?? item?.Precision, 10);
            return Number.isNaN(value) ? 0 : Math.max(0, value);
          })(),
          data: labels.map((label) => {
            const row = rows.find((value) => (value?.Label ?? value?.label) == label);
            return Number(row?.Value ?? row?.value ?? 0);
          }),
        };
      });

      return {
        labels,
        data: datasets[0]?.data || [],
        datasets,
      };
    },

    refreshClicked() {
      this.getData();
      this.contextMenuIsOpen = false;
    },

    getWidgetDataRefreshKey(widget = {}) {
      const fieldKey = (field) => (typeof field == 'object' ? field?.key : field);
      const filterKey = (filter) => ({
        key: filter?.key,
        filterType: filter?.filterType,
        value: filter?.value,
        selected: filter?.selected,
        from: filter?.from,
        to: filter?.to,
      });

      return JSON.stringify({
        key: widget.key,
        widgetType: widget.widgetType,
        detailsFields: widget.detailsFields?.map(fieldKey),
        primaryLeftField: fieldKey(widget.primaryLeftField),
        primaryRightField: fieldKey(widget.primaryRightField),
        sortKey: widget.sortKey,
        sortDesc: widget.sortDesc,
        maxRows: widget.maxRows,
        chartGroup: widget.chartGroup,
        dataToShow: fieldKey(widget.dataToShow),
        chartType: widget.chartType,
        chartTime: widget.chartTime,
        templateKey: widget.templateKey,
        statsToShow: widget.statsToShow?.map(fieldKey),
        filtersArr: widget.filtersArr?.map(filterKey),
      });
    },

    getIcon(url) {
      const getFirstSplit = url.split('/').filter((item) => item)[0];
      const findMenu = this.subMenuIcons.find((item) => item.module.toLowerCase() == getFirstSplit.toLowerCase());
      return findMenu?.icon ?? '';
    },

    // dragable button edits

    onButtonConfigItemDragStart(index) {
      this.buttonDragIndex = index;
    },
    onButtonConfigItemDragOver(index) {
      if (this.buttonDragIndex === null || this.buttonDragIndex === index) return;

      const cols = [...this.buttonFieldsEdit];
      const dragged = cols.splice(this.buttonDragIndex, 1)[0];
      cols.splice(index, 0, dragged);

      this.buttonFieldsEdit = cols;
      this.buttonDragIndex = index;
      this.markTemplateCustom();
    },
    onButtonConfigItemDragEnd() {
      this.buttonDragIndex = null;
    },

    // end drag
    editNewButtonSelected(e) {
      if (!this.buttonFieldsEdit || !e) return;
      const value = this.editNewButtonValue;
      const name = value.label.includes('] ') ? value.label.split('] ')[1] : value.label;
      this.buttonFieldsEdit.push({ ...value, label: name });
      this.markTemplateCustom();
      this.$nextTick(() => {
        this.editNewButtonValue = null;
        this.editNewButtonSearch = '';
      });
    },

    buttonConfigDelete(i) {
      this.buttonFieldsEdit.splice(i, 1);
      this.markTemplateCustom();
    },

    async dashboardSearch() {
      if (this.searchLoading) return;
      this.searchValueError = '';
      const trim = this.searchValue.trim();
      if (trim.length < 2) {
        this.searchValueError = 'Search term must be at least 2 characters long.';
        return;
      }
      this.searchLoading = true;
      const result = await this.$apiCallV2('/Dashboard/DashboardSearch', { searchterm: trim }, 'POST', {
        message: `Search API failed`,
      });
      this.searchResultList = result ?? [];
      this.searchLoading = false;
      this.activeSearchValue = trim;
      this.$nextTick(() => {
        if (this.$refs.searchList) this.$refs.searchList.scrollTop = 0;
      });
    },

    checkPrimaryHighLight(text) {
      if (!text) return false;
      return String(text).toLowerCase().includes(this.activeSearchValue.toLowerCase());
    },

    highlightText(text) {
      if (!text) return '';
      const search = this.activeSearchValue;
      const regex = new RegExp(`(${search})`, 'gi');
      return String(text).replace(regex, '<span style="background-color:yellow">$1</span>');
    },

    formatAppends(text) {
      if (!text) return '';
      return String(text).replace(
        /(\([^)]*\))/g,
        '<span class="append-data" style="font-size: 0.85rem; color: gray;">$1</span>',
      );
    },

    onStatsConfigItemDragStart(index) {
      this.statsDragIndex = index;
    },
    onStatsConfigItemDragOver(index) {
      if (this.statsDragIndex === null || this.statsDragIndex === index) return;

      const cols = [...this.statsFieldsEdit];
      const dragged = cols.splice(this.statsDragIndex, 1)[0];
      cols.splice(index, 0, dragged);

      this.statsFieldsEdit = cols;
      this.statsDragIndex = index;
      this.markTemplateCustom();
    },
    onStatsConfigItemDragEnd() {
      this.statsDragIndex = null;
    },
    statsFieldDelete(i) {
      this.statsFieldsEdit.splice(i, 1);
      this.markTemplateCustom();
    },
    editNewStatFieldSelected(e) {
      if (e) {
        this.statsFieldsEdit.push({ ...e, id: crypto.randomUUID() });
        this.markTemplateCustom();
      }
      this.$nextTick(() => {
        this.editNewStatFieldInputValue = '';
      });
    },

    async getNofitications() {
      const result = await this.getDashboardApiData(
        '/Dashboard/GetNotifications',
        {},
        'Failed to get notifications',
      );
      if (!this.apiErrorMessage) this.notificationsList = result || [];
    },

    async updateNotificationIsRead(id, isread) {
      this.notificationsList = this.notificationsList.map((item) => {
        if (item.NotifID == id) {
          item.IsRead = isread == '1';
        }
        return item;
      });
      await this.$apiCallV2('/Dashboard/UpdateNotificationIsRead', { notifid: id, isread: isread }, 'POST', {
        message: `Failed to update notifications isRead`,
      });
    },

    async updateAllNotificationsIsRead() {
      this.notificationsList = this.notificationsList.map((item) => {
        return { ...item, IsRead: true };
      });
      await this.$apiCallV2('/Dashboard/UpdateAllNotificationIsRead', {}, 'POST', {
        message: `Failed to update notifications isRead`,
      });
    },
  },
  mounted() {
    this.userParam = this.$route.params['user'];
    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('click', this.handleOutsideDetailsMenuClick);

  },
  beforeDestroy() {
    this.dataLoadRequestId++;
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('click', this.handleOutsideDetailsMenuClick);
  },
  watch: {
    widgetObject: {
      async handler(val, oldVal) {
        if (!val?.key || !val?.widgetType) return;
        if (this.skipNextWidgetDataReload) {
          this.skipNextWidgetDataReload = false;
          return;
        }
        if (oldVal && this.getWidgetDataRefreshKey(val) == this.getWidgetDataRefreshKey(oldVal)) return;
        const requestId = ++this.dataLoadRequestId;
        await this.loadDashboardOptionsMetadata(val.key);
        if (this._isDestroyed || requestId != this.dataLoadRequestId) return;
        if (this.migrateSavedDashboardWidget(val.key)) return;
        this.getData();
      },
      deep: true,
      immediate: true,
    },
  },

  errorCaptured(err, info) {
    // eslint-disable-next-line no-console
    console.error(err, info);
    this.hasError = true;
    return false;
  },
};
</script>

<style lang="scss">
@import '@/css/all.scss';

.dashboard-item {
  position: relative;
  background-color: white;
  height: 100%;
  min-height: 0;
  max-height: none;
  font-size: 0.75rem;
  border: 1px solid #b0bad0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  contain: layout style;
  > * {
    min-height: 0;
  }

  .dashboard-widget-error {
    margin: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5b8b8;
    background-color: #fff7f7;
    color: #7f1d1d;
    font-size: 0.7rem;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
  .header-container {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-shrink: 0;
    padding: 0.25rem 0.5rem;

    min-height: 2.25rem;
    max-height: 2.25rem;

    border-bottom: 1px solid lightgray;
    color: #2d3348;
    .title {
      flex: 1 1 auto;
      min-width: 0;
      align-self: stretch;
      font-weight: 600;
      font-size: 0.75rem;
      display: flex;
      gap: 8px;
      align-items: center;
      line-height: 1;
      word-spacing: -2px;
      cursor: grab;
      touch-action: none;
    }

    .widget-title-text {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      min-width: 0;
    }

    .widget-filter-subtext {
      color: #6b7280;
      font-size: 0.6rem;
      font-weight: normal;
      line-height: 1;
      word-spacing: 0;
    }

    .menu-btn {
      cursor: pointer;
    }

  }

  .widget-resize-handle {
    position: absolute;
    right: 0.15rem;
    bottom: 0.15rem;
    width: 0.75rem;
    height: 0.75rem;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: nwse-resize;
    touch-action: none;
    opacity: 0.72;
    z-index: 4;
    transition: opacity 0.12s ease;
    pointer-events: auto;

    .widget-resize-icon {
      position: absolute;
      right: 0.05rem;
      bottom: 0.05rem;
      display: block;
      width: 0.5rem;
      height: 0.5rem;
      border-right: 2px solid #6c8bfd;
      border-bottom: 2px solid #6c8bfd;
    }
  }

  &:hover .widget-resize-handle,
  &:focus-within .widget-resize-handle {
    opacity: 1;
  }

  .context-menu-wrapper {
    position: relative;

    .context-menu-list {
      --direction-offset: 0.25rem;

      position: absolute;
      top: 100%;
      right: -0.75rem;
      width: max-content;
      background-color: white;
      border-radius: 2px;
      box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
      outline: 1px solid rgba(0, 0, 0, 0.1);
      z-index: 1;
      font-size: 0.75rem;

      padding: 0.25rem 0;

      top: calc(100% + var(--direction-offset));

      .context-menu-item {
        display: flex;
        width: 100%;
        border: 0;
        background: transparent;
        text-align: left;
        color: inherit;
        text-decoration: inherit;
        cursor: pointer;
        padding: 0.25rem 0.75rem 0.25rem 0.5rem;

        &:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        &:focus-visible {
          outline: 2px solid #6c8bfd;
          outline-offset: -2px;
        }
      }

      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      transition: 0s;
    }

    &.active {
      .context-menu-list {
        visibility: visible;
        opacity: 1;
        pointer-events: all;
        transition: all 0.1s ease-out;
      }
    }
  }
  .edit-container {
    padding: 0 0.35rem;
    padding-top: 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    height: 100%;
    overflow: auto;

    .edit-divider {
      width: 100%;
      margin: 0.35rem 0;
      border: 0;
      border-top: 1px solid #c8d0e8;
    }

    .widget-template-config {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }

    .cta-group {
      padding: 0.35rem 0;
      background-color: white;
      display: flex;
      justify-content: end;
      gap: 0.5rem;
      margin-top: auto;
      position: sticky;
      bottom: -1px;
    }

    > *:nth-last-child(2) {
      margin-bottom: 0.5rem;
    }

    .edit-widget-fields {
      gap: 0.2rem;
    }

    .chart-time-config {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;

      .chart-time-range {
        display: flex;
        gap: 0.5rem;

        .chart-time-picker {
          min-width: 0;
        }
      }

      .chart-time-days {
        display: flex;
        align-items: center;
        gap: 0.35rem;

        .chart-time-days-input {
          width: 100%;
        }
      }
    }

    .daily-report-default-filters {
      padding: 0.35rem 0.5rem;
      border-left: 3px solid #c8d0e8;
      color: #6b7280;
      font-size: 0.65rem;
      line-height: 1.25;

      .daily-report-default-filters-title {
        margin-bottom: 0.15rem;
        font-weight: 600;
      }

      .daily-report-default-filter {
        display: flex;
        align-items: flex-start;
        gap: 0.25rem;
      }

      .daily-report-default-filter-label {
        font-weight: 600;
      }
    }

    .list-fields-editor {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.35rem;
      border: 1px solid #c8d0e8;
      border-radius: 2px;
      background-color: #f8f9fc;

      .field-config {
        border: none;
        padding: 0;
      }
    }

    .widget-customization-group {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      margin: 0 0 0.5rem;
    }

    .field-config {
      display: flex;

      gap: 0.35rem;
      flex-wrap: wrap;

      border-radius: 2px;
      border: 1px solid lightgray;
      padding: 0.5rem;

      .field-config-item {
        display: flex;
        gap: 0.4rem;
        align-items: center;

        border-radius: 2px;
        padding: 0.25rem 0.35rem 0.25rem 0.5rem;
        background-color: #b3c4ff;
        width: fit-content;
        touch-action: none;

        .field-config-item-label {
          cursor: grab;
        }

        .field-config-item-delete {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          width: 16px;
          height: 16px;
          margin-left: auto;
          padding: 0;
          border: none;
          background: transparent;
        }

        @media (hover: hover) {
          .field-config-item-delete {
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.15s ease;
          }

          &:hover .field-config-item-delete,
          &:focus-within .field-config-item-delete {
            opacity: 1;
            pointer-events: auto;
          }
        }

        &.dragging {
          opacity: 0.5;
          cursor: grabbing;
        }
      }
    }

    .list-field-config {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      grid-auto-flow: dense;
      align-items: stretch;

      .field-config-item {
        width: 100%;
        min-width: 0;
      }

      .field-config-item-label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .buttons-config {
      display: grid;
      gap: 0.25rem;
      grid-template-columns: repeat(2, 1fr);
      grid-auto-rows: 2.5rem;
      padding: 0.25rem;
      border: 1px solid lightgray;

      .buttons-config-item {
        background-color: #b3c4ff;
        width: 100%;
        height: 100%;
        border-radius: 2px;
        font-size: 0.75rem;
        line-height: 1.15;
        min-height: 2.5rem;
        cursor: grab;

        &.dragging {
          opacity: 0.5;
          cursor: grabbing;
        }
      }
    }
  }

  .details-list {
    overflow: hidden;
    padding: 0.35rem 0.35rem;
    margin-top: 0;
    flex-grow: 1;
    overflow: auto;

    height: 100%;

    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    .details-item {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
      padding-right: 0.25rem;

      border: none;
      border-radius: 2px;
      background-color: #ffffff;
      border: 1px solid #c8d0e8;
      border-left: 3px solid #b3c4ff;

      .item-details {
        padding: 0.25rem 0.25rem 0.25rem 0.5rem;
        width: 100%;
      }

      .primary-fields-row {
        display: flex;
        align-items: center;
        min-height: 2rem;
        padding-bottom: 0.25rem;

        .item-actions {
          margin-left: 1rem;
        }
      }

      .field-item {
        line-height: 1.25;
      }
      .field-item-label {
        font-size: 0.6rem;
        word-spacing: -2px;
        line-height: 1.5;
        color: #6b7280;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        line-clamp: 1;
        -webkit-line-clamp: 1;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .jobno-text {
        margin-right: auto;
      }

      .jobdate-text {
        min-width: 20%;
      }

      .jobno-text,
      .jobdate-text {
        padding-top: 0.15rem;
      }

      .details-fields {
        display: grid;
        grid-template-rows: 0fr;
        > div {
          overflow: hidden;
        }

        .item-actions {
          display: flex;
          align-items: end;
          padding-bottom: 0.45rem;
        }

        &.open {
          grid-template-rows: 1fr;

          .menu-btn img {
            transform: rotate(180deg);
          }
        }
      }

      .details-fields-inner {
        min-height: calc(0.25rem + 2.5rem);
        border-top: 1px solid #eef1f8;
        padding-top: 0.25rem;
        display: flex;
        .item-actions {
          margin-left: 0.25rem;
        }
      }

      .details-fields-list {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        grid-auto-flow: dense;
        gap: 0.25rem;
        flex-grow: 1;
        .field-item {
          padding: 0.2rem 0.4rem;
          background-color: #fafbfc;
          border: 1px solid transparent;
          border-radius: 2px;
          outline: 1px solid #d8dde7;
          outline-offset: -1px;
        }

        .field-item-text {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
          text-overflow: ellipsis;
        }
      }

      .details-fields.open {
        .details-fields-list {
          .field-item-text {
            line-clamp: 3;
            -webkit-line-clamp: 3;
          }
        }
      }
    }
  }

  .menu-btn {
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    user-select: none;
    font-weight: bold;
    font-size: 1rem;
    max-height: 100%;
    height: 24px;
    width: 24px;
    border-radius: 50%;
    cursor: pointer;
    &:active,
    &:focus {
      outline: none;
      border: none;
      box-shadow: none;
    }
    position: relative;

    &:hover {
      background-color: #dddddd;
    }
  }

  .buttons-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    gap: 0.35rem;
    padding: 0.35rem;
    overflow: auto;
    min-height: 0;
    max-height: 100%;
    align-content: start;
    .button-item {
      padding: 0.5rem;
      // padding-right: 0.25rem;
      border-radius: 2px;
      background-color: #f0f3ff;
      border: 1px solid #dde5ff;
      color: inherit;
      text-decoration: none;

      display: flex;
      align-items: center;
      min-height: 3rem;
      gap: 0.5rem;
      transition: none;

      &:hover {
        background-color: #e8eeff;
        border-color: #aebfff;
      }

      .btn-img {
        width: 2rem;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        flex-shrink: 0;
      }

      .btn-text {
        height: auto;
        word-spacing: -2px;
        line-height: 1.05;
        font-size: 0.75rem;
      }

      .btn-category {
        margin-top: 0.15rem;
        color: #5a6380;
        font-size: 0.6rem;
        word-spacing: -2px;
        line-height: 1;
      }
    }
  }

  .search-widget {
    min-height: 0;
    height: 100%;
    overflow: hidden;
    .search-initial {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: #f2f7fd;
      border-radius: 2px;
      padding: 1rem 10%;
      font-size: 0.85rem;
      outline: 1px solid #ececec;
      text-align: center;
      .search-img {
        width: 6rem;
        height: 6rem;
        flex: 0 0 6rem;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        margin-bottom: 1rem;
      }
      input {
        padding: 0.5rem;
        height: 2.25rem;
        margin-top: 0.5rem;
        text-align: center;
      }

      button {
        width: 100%;
        font-size: 0.85rem;
        margin-top: 0.5rem;
      }
    }

    .search-list-container {
      padding: 0.35rem;
      padding-top: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      .search-row {
        position: sticky;
        top: 0;
        background-color: white;
        padding: 0.25rem 0;

        .search-row-wrapper {
          display: flex;
          gap: 0.5rem;
          input {
            padding-left: 0.5rem;
          }
          button {
            width: 40%;
          }
        }
      }
      flex-grow: 1;
      overflow: auto;
      .search-list {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        .search-item {
          padding: 0.5rem 0.5rem;
          border: 1px solid lightgray;
          border-radius: 2px;
          // background-color: #fafcff;
          word-spacing: -2px;
          .search-item-primary-row {
            display: flex;
            gap: 0.25rem;
            align-items: center;
          }
          .action-buttons-list {
            display: flex;
            gap: 0.25rem;
            flex-wrap: wrap;
            align-items: center;
            margin-left: 0.5rem;
          }
          .action-buttons {
            font-weight: bold;
            color: #6c8bfd;
            background-color: #f5f9ff;
            cursor: pointer;
            padding: 0.15rem 0.35rem;
            border-radius: 4px;
            transition: background-color 0.15s ease;
            word-spacing: -2px;
            &:hover {
              background-color: #dae8fc;
            }
          }

          .field-item {
            line-height: 1.25;
          }
          .field-item-label {
            font-size: 0.6rem;
            word-spacing: -2px;
            line-height: 1.5;
            color: #252831;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            line-clamp: 1;
            -webkit-line-clamp: 1;
            text-overflow: ellipsis;
            overflow: hidden;
          }
          .field-item-text {
            overflow: hidden;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            line-clamp: 1;
            -webkit-line-clamp: 1;
            text-overflow: ellipsis;
          }

          .search-item-details {
            margin-top: 0.25rem;
            padding-top: 0.5rem;
            border-top: 1px solid lightgray;
            display: flex;
            gap: 0.25rem;
            row-gap: 0.15rem;
            flex-wrap: wrap;

            .search-item-details-item {
              border: 1px solid lightgray;
              background-color: #fafcff;
              padding: 0.25rem 0.5rem;
              padding-right: 1rem;
              border-radius: 4px;
            }
          }
        }
      }
    }

    input {
      width: 100%;
      border: 2px solid #dfdfdf;
      border-radius: 4px;
    }

    button {
      width: 100%;
    }
  }

  &.dashboard-item-wide {
    .details-fields-list {
      grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
    }

    .buttons-list {
      grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    }

    .stats-container {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .widget-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    text-align: center;
  }

  &::-webkit-scrollbar {
    width: 4px;
    height: 2px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    border: none;
    box-shadow: none;
  }
  &::-webkit-scrollbar-thumb {
    border: none;
    box-shadow: none;
  }

  * {
    &::-webkit-scrollbar {
      width: 4px;
      height: 2px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
      border: none;
      box-shadow: none;
    }
    &::-webkit-scrollbar-thumb {
      border: none;
      box-shadow: none;
    }
  }

  .chart-container {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-height: 0;
    height: 100%;
    .chart-wrapper {
      padding: 0.5rem;
      min-height: 0;
      flex-grow: 1;

      &.chart-wrapper-doughnut-tall {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
    }
  }

  .notification-list {
    padding: 0.35rem;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    height: 100%;
    .notification-item {
      border-radius: 4px;
      border: 1px solid lightgray;
      overflow: hidden;
      flex-shrink: 0;
      .notification-title-row {
        display: flex;
        gap: 0.25rem;
        padding: 0.15rem 0.5rem;
        border-bottom: 1px solid lightgray;

        .notification-item-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: bold;
          margin-right: auto;
          img {
            height: 16px;
          }
        }

        .unread-badge {
          font-size: 0.65rem;
          font-weight: normal;
          background-color: rgb(250, 91, 91);
          color: white;
          border-radius: 2rem;
          padding: 0.05rem 0.5rem;
          display: flex;
          align-items: center;
        }
      }

      .notification-details {
        padding: 0.55rem 0.65rem;
        line-height: 1.15;
        word-spacing: -2px;
        display: flex;
        // align-items: center;
        gap: 0.65rem;
        position: relative;
        > img {
          margin-top: 0.15rem;
        }
        .actions {
          display: none;
          position: absolute;
          top: 0;
          right: 0;
          height: 100%;
          background: linear-gradient(270deg, var(--color-light) 50%, transparent 250%);
          padding: 0rem 1rem;
          margin-left: auto;
          // display: flex;
          gap: 0.75rem;
          img {
            object-fit: contain;
          }

          .action-button {
            cursor: pointer;
            display: flex;
            align-items: center;
            flex-shrink: 0;
            &:hover {
              opacity: 0.65;
            }

            > * {
              height: 100%;
              display: flex;
              align-items: center;
            }
          }
        }
      }

      &:hover {
        .actions {
          display: flex !important;
        }
      }
    }
    .notification-item {
      --color: #b8cfe0;
      --color-light: #f2f7fd;
      --color-mid: #daeaf4;
      --color-dark: #2a4a5e;

      border: 1px solid var(--color);

      transition: border-color 0.05s ease;

      &.info {
        --color: #a8c4e0;
        --color-light: #f2f7fd;
        --color-mid: #d8eaf8;
        --color-dark: #1a3a58;
      }
      &.success {
        --color: #a8cfc0;
        --color-light: #f2faf7;
        --color-mid: #d8eeea;
        --color-dark: #1a3a30;
      }
      &.warning {
        --color: #d4c0a0;
        --color-light: #fdf8f2;
        --color-mid: #f0e4cc;
        --color-dark: #3a2a10;
      }
      &.urgent {
        --color: #d4b0bc;
        --color-light: #fdf2f4;
        --color-mid: #f0d8de;
        --color-dark: #3a1a22;
      }

      &.is-unread {
        border-color: var(--color-dark);
        border-width: 1.5px;
      }

      &.info.is-unread {
        border-color: #2874b8;
      }
      &.success.is-unread {
        border-color: #1a8a64;
      }
      &.warning.is-unread {
        border-color: #b87820;
      }
      &.urgent.is-unread {
        border-color: #b83050;
      }

      .notification-title-row {
        background-color: var(--color-mid);
        border-bottom-color: var(--color);
        color: var(--color-dark);
      }

      .notification-details {
        background-color: var(--color-light);
        color: var(--color-dark);
      }
    }
  }

  .stats-container {
    padding: 0.35rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.25rem;
    overflow: auto;
  }

  .daily-report-widget-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;

    .daily-report-widget {
      flex: 1 1 auto;
    }
  }

  .daily-report-widget-footer {
    flex-shrink: 0;
    padding: 0.35rem;
    border-top: 1px solid #e2e8f0;
    background-color: white;
  }

  .daily-report-popup-button {
    width: 100%;
    min-height: 2rem;
  }

  .stats-item {
    background-color: #f5f8ff;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.5rem;
    padding-left: 0.75rem;
    gap: 0.15rem;
    display: flex;
    flex-direction: column;
    min-height: 4rem;

    .stats-number {
      font-size: 1.35rem;
      font-weight: 700;
      color: #363d57;
      line-height: 1.15;
      word-spacing: -2px;
      &.no-data {
        transform: scale(0.85);
        transform-origin: left;
      }
    }

    .stats-label {
      font-size: 0.7rem;
      color: #4a5270;
      word-spacing: -2px;
      line-height: 1.1;
    }
  }

  .stats-config {
    .stats-field-config {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.25rem;
    }

    .stats-field-config-item {
      font-size: 0.7rem;
      line-height: 1.15;
      width: 100% !important;
    }
  }

  button {
    transition: none;
  }
}

.no-data {
  color: darkgray !important;
  font-style: italic;
}
.flex-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

</style>
